import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Calendar,
  Clock,
  Settings,
  Save,
  Plus,
  Edit3,
  Trash2,
  X,
  FileText,
  Package,
  Upload,
  CheckCircle,
  AlertCircle,
  DollarSign,
  Mail,
  MessageSquare,
  ArrowUp,
  ArrowDown,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Hash,
  Receipt,
  Bell,
  Building,
  Search,
  Filter,
  Users
} from 'lucide-react';
import axios from 'axios';

const ManageTables = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('manage-tables');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Manage Tables States
  const [tables, setTables] = useState([]);
  const [tableSchedules, setTableSchedules] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
  const [showTableModal, setShowTableModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showDateModal, setShowDateModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [editingDateEntry, setEditingDateEntry] = useState(null);
  const [showBlockDateModal, setShowBlockDateModal] = useState(false);
  const [dateToBlock, setDateToBlock] = useState(null);
  const [tableToBlock, setTableToBlock] = useState(null);
  const [showDayDetailModal, setShowDayDetailModal] = useState(false);
  const [selectedDayDetail, setSelectedDayDetail] = useState(null);
  
  // Calendar View States
  const [reservations, setReservations] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [calendarView, setCalendarView] = useState('list'); // 'list' or 'calendar'
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selectedServiceOptions, setSelectedServiceOptions] = useState({});
  const [modalDataReady, setModalDataReady] = useState(false);
  const [modalServices, setModalServices] = useState([]);
  const [modalTables, setModalTables] = useState([]);
  const [filteredTables, setFilteredTables] = useState([]);
  const [showDeleteAllBookingsModal, setShowDeleteAllBookingsModal] = useState(false);
  const [showDeleteAllToursModal, setShowDeleteAllToursModal] = useState(false);
  const [showBlockSlotModal, setShowBlockSlotModal] = useState(false);
  const [slotToBlock, setSlotToBlock] = useState(null);
  const [openAccordions, setOpenAccordions] = useState({
    slotBookings: true,
    appointmentCounter: false,
    payments: false,
    notifications: false,
    taxSettings: false
  });

  const toggleAccordion = (section) => {
    setOpenAccordions(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Table Categories States
  const [tableCategories, setTableCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ category_name: '', description: '' });
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [showDeleteTableModal, setShowDeleteTableModal] = useState(false);
  const [tableToDelete, setTableToDelete] = useState(null);
  const [dateForm, setDateForm] = useState({
    is_24_hours: false,
    start_time: '09:00',
    end_time: '17:00',
    slot_duration_minutes: 30,
    enable_slot_booking: false,
    breaks: []
  });
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [tempBlockedDates, setTempBlockedDates] = useState([]);
  const [tableForm, setTableForm] = useState({
    table_name: '',
    capacity: 2,
    description: '',
    category_id: '',
    schedule_duration_months: 1,
    is_24_hours: false,
    start_time: '09:00',
    end_time: '17:00',
    slot_duration_minutes: 30,
    enable_slot_booking: false,
    bulk_start_date: '',
    bulk_end_date: '',
    breaks: []
  });
  const [scheduleForm, setScheduleForm] = useState({
    start_time: '09:00',
    end_time: '17:00',
    slot_duration_minutes: 30,
    is_enabled: true,
    breaks: []
  });

  // Configure Form States
  const [formFields, setFormFields] = useState([]);
  const [roomFields, setRoomFields] = useState([]);
  const [specialInstructionFields, setSpecialInstructionFields] = useState([]);
  const [tourFields, setTourFields] = useState([]);
  const [selectedSection, setSelectedSection] = useState('form');
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [showDeleteFieldModal, setShowDeleteFieldModal] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);
  const [fieldForm, setFieldForm] = useState({
    field_name: '',
    field_type: 'text',
    required: false,
    placeholder: '',
    description: '',
    options: ''
  });

  // Section Enable/Disable States
  const [roomDetailsEnabled, setRoomDetailsEnabled] = useState(true);
  const [specialInstructionsEnabled, setSpecialInstructionsEnabled] = useState(true);

  // Configure Services States
  const [servicesEnabled, setServicesEnabled] = useState(true);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryServices, setCategoryServices] = useState({});
  const [serviceCategoryMappings, setServiceCategoryMappings] = useState({}); // { serviceId: [categoryId1, categoryId2] }

  // Document Fields States
  const [documentFields, setDocumentFields] = useState([]);
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [documentForm, setDocumentForm] = useState({
    title: '',
    description: ''
  });

  // Settings States
  const [settings, setSettings] = useState({
    slot_duration: 15,
    start_time: '09:00',
    end_time: '17:00',
    dropoff_start_time: '07:00',
    dropoff_end_time: '19:00',
    pickup_start_time: '07:00',
    pickup_end_time: '19:00',
    working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    services_enabled: true,
    payment_enabled: false,
    payment_mode: '',
    token_amount: 0,
    email_enabled: false,
    sms_enabled: false,
    document_upload_enabled: false,
    table_number_prefix: 'TBL',
    table_number_start: 1000
  });

  // Payment Gateway States
  const [availablePaymentModes, setAvailablePaymentModes] = useState([]);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const primaryColor = 'var(--theme-primary)';

  const tabs = [
    { id: 'manage-tables', label: 'Manage Tables', icon: Building },
    { id: 'configure-form', label: 'Configure Form', icon: FileText },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const fieldTypes = [
    { value: 'text', label: 'Text Input' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone Number' },
    { value: 'date', label: 'Date' },
    { value: 'dropdown', label: 'Dropdown' },
    { value: 'time', label: 'Time (24hr)' },
    { value: 'radio', label: 'Radio Button' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'description', label: 'Description Text' }
  ];

  const sections = [
    { value: 'form', label: 'Form Details' },
    { value: 'special_instructions', label: 'Special Instructions' }
  ];

  const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    if (token) {
      fetchTableCategories();
      fetchTables();
      fetchReservations();
      fetchTableSchedules();
      fetchBlockedDates();
      fetchFormFields();
      fetchSettings();
      fetchServiceCategories();
      fetchPaymentSettings();
      fetchDocumentFields();
      initializeDefaultFields();
    }
  }, [token]);

  // Helper function to calculate stay duration in hours
  const calculateStayDuration = (dropoffDate, dropoffTime, pickupDate, pickupTime) => {
    if (!dropoffDate || !dropoffTime || !pickupDate || !pickupTime) {
      return 0;
    }
    
    const checkIn = new Date(`${dropoffDate}T${dropoffTime}`);
    const checkOut = new Date(`${pickupDate}T${pickupTime}`);
    
    const durationMs = checkOut - checkIn;
    const durationHours = durationMs / (1000 * 60 * 60);
    
    return Math.max(0, durationHours);
  };

  // Helper function to calculate variant price based on duration
  const calculateVariantPrice = (service, durationHours) => {
    if (!service?.is_variable || !service?.variants || service.variants.length === 0) {
      return service?.price || 0;
    }

    if (durationHours <= 0) {
      return 0;
    }

    const variants = service.variants
      .filter(v => v.is_enabled)
      .map(v => {
        const variantName = v.variant_name.toLowerCase().replace(/\s+/g, '');
        let hours = 0;
        
        if (variantName.includes('hr')) {
          const match = variantName.match(/(\d+\.?\d*)hr/);
          if (match) {
            hours = parseFloat(match[1]);
          }
        } else if (variantName.includes('min')) {
          const match = variantName.match(/(\d+\.?\d*)min/);
          if (match) {
            hours = parseFloat(match[1]) / 60;
          }
        }
        
        return { hours, price: v.price, name: v.variant_name };
      })
      .sort((a, b) => b.hours - a.hours);

    let remainingHours = durationHours;
    let totalPrice = 0;

    for (const variant of variants) {
      if (variant.hours > 0) {
        const count = Math.floor(remainingHours / variant.hours);
        if (count > 0) {
          totalPrice += count * variant.price;
          remainingHours -= count * variant.hours;
        }
      }
    }

    if (remainingHours > 0 && variants.length > 0) {
      const smallestVariant = variants[variants.length - 1];
      const additionalCount = Math.ceil(remainingHours / smallestVariant.hours);
      totalPrice += additionalCount * smallestVariant.price;
    }

    return totalPrice;
  };

  // Helper function to get variant breakdown
  const getVariantBreakdown = (service, durationHours) => {
    if (!service?.is_variable || !service?.variants || service.variants.length === 0) {
      return [];
    }

    if (durationHours <= 0) {
      return [];
    }

    const variants = service.variants
      .filter(v => v.is_enabled)
      .map(v => {
        const variantName = v.variant_name.toLowerCase().replace(/\s+/g, '');
        let hours = 0;
        
        if (variantName.includes('hr')) {
          const match = variantName.match(/(\d+\.?\d*)hr/);
          if (match) {
            hours = parseFloat(match[1]);
          }
        } else if (variantName.includes('min')) {
          const match = variantName.match(/(\d+\.?\d*)min/);
          if (match) {
            hours = parseFloat(match[1]) / 60;
          }
        }
        
        return { hours, price: v.price, name: v.variant_name };
      })
      .sort((a, b) => b.hours - a.hours);

    let remainingHours = durationHours;
    const breakdown = [];

    for (const variant of variants) {
      if (variant.hours > 0) {
        const count = Math.floor(remainingHours / variant.hours);
        if (count > 0) {
          remainingHours -= count * variant.hours;
          breakdown.push({ ...variant, count });
        }
      }
    }

    if (remainingHours > 0 && variants.length > 0) {
      const smallestVariant = variants[variants.length - 1];
      const additionalCount = Math.ceil(remainingHours / smallestVariant.hours);
      const existingEntry = breakdown.find(b => b.name === smallestVariant.name);
      if (existingEntry) {
        existingEntry.count += additionalCount;
      } else {
        breakdown.push({ ...smallestVariant, count: additionalCount });
      }
    }

    return breakdown;
  };

  // Helper function to get field name from ID
  const getFieldNameById = (fieldId, section = 'form') => {
    let fields = [];
    if (section === 'form') fields = formFields;
    else if (section === 'room') fields = roomFields;
    else if (section === 'special') fields = specialInstructionFields;
    
    const field = fields.find(f => f.id === fieldId);
    return field?.field_name || fieldId;
  };

  const initializeDefaultFields = () => {
    // Initialize default First Name and Last Name fields for form section
    const defaultFormFields = [
      {
        id: 'default_first_name',
        field_name: 'First Name',
        field_type: 'text',
        required: true,
        is_system: true,
        section: 'form',
        order: 0
      },
      {
        id: 'default_last_name',
        field_name: 'Last Name',
        field_type: 'text',
        required: true,
        is_system: true,
        section: 'form',
        order: 1
      }
    ];
    
    // Initialize default First Name and Last Name fields for tour section
    const defaultTourFields = [
      {
        id: 'default_tour_first_name',
        field_name: 'First Name',
        field_type: 'text',
        required: true,
        is_system: true,
        section: 'tour',
        order: 0
      },
      {
        id: 'default_tour_last_name',
        field_name: 'Last Name',
        field_type: 'text',
        required: true,
        is_system: true,
        section: 'tour',
        order: 1
      }
    ];
    
    setFormFields(prev => {
      const hasDefaults = prev.some(f => f.is_system);
      if (!hasDefaults && prev.length === 0) {
        return defaultFormFields;
      }
      return prev;
    });
    
    setTourFields(prev => {
      const hasDefaults = prev.some(f => f.is_system);
      if (!hasDefaults && prev.length === 0) {
        return defaultTourFields;
      }
      return prev;
    });
  };

  // Manage Tables Functions
  const fetchTableCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/table-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.categories) {
        setTableCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Error fetching table categories:', error);
    }
  };

  const fetchTables = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tables`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.tables) {
        setTables(response.data.tables);
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
    }
  };

  const fetchReservations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/table-reservations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.reservations) {
        setReservations(response.data.reservations);
      }
    } catch (error) {
      console.error('Error fetching reservations:', error);
    }
  };

  // Helper to check if a table is reserved on a specific date
  const getReservationsForTableAndDate = (tableId, date) => {
    const dateStr = formatDateForAPI(date);
    return reservations.filter(res => {
      if (res.assigned_table_id !== tableId) return false;
      
      // Check if this date falls within the reservation period
      const dropoffDate = res.dropoff_date;
      const pickupDate = res.pickup_date;
      
      return dateStr >= dropoffDate && dateStr <= pickupDate;
    });
  };

  // Helper to check if a table has any reservations on a date
  const isTableReserved = (tableId, date) => {
    return getReservationsForTableAndDate(tableId, date).length > 0;
  };

  // Generate time slots based on settings
  const generateTimeSlots = () => {
    if (!settings) return [];
    
    const slots = [];
    const startTime = settings.start_time || '09:00';
    const endTime = settings.end_time || '22:00';
    const interval = settings.slot_duration || 30;
    
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    let currentHour = startHour;
    let currentMin = startMin;
    
    while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
      const timeStr = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
      slots.push(timeStr);
      
      currentMin += interval;
      if (currentMin >= 60) {
        currentHour += Math.floor(currentMin / 60);
        currentMin = currentMin % 60;
      }
    }
    
    return slots;
  };

  // Helper to check if time slot falls within reservation duration
  const addMinutesToTime = (timeStr, minutes) => {
    const [hour, min] = timeStr.split(':').map(Number);
    const date = new Date(2000, 0, 1, hour, min);
    date.setMinutes(date.getMinutes() + minutes);
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  const timesOverlap = (start1, end1, start2, end2) => {
    return start1 < end2 && start2 < end1;
  };

  // Check if a specific time slot is booked for a table on a date
  const isTimeSlotBooked = (tableId, date, timeSlot) => {
    const dateStr = formatDateForAPI(date);
    const slotDuration = settings?.slot_duration || 30;
    const slotEnd = addMinutesToTime(timeSlot, slotDuration);
    
    const reservationsOnDate = reservations.filter(res => {
      if (res.assigned_table_id !== tableId || res.dropoff_date !== dateStr) {
        return false;
      }
      
      // Check if this time slot overlaps with the reservation
      const resDuration = res.duration_minutes || 60;
      const resStart = res.dropoff_time;
      const resEnd = addMinutesToTime(resStart, resDuration);
      
      return timesOverlap(timeSlot, slotEnd, resStart, resEnd);
    });
    
    return reservationsOnDate.length > 0 ? reservationsOnDate[0] : null;
  };

  // Handle day cell click to show time slot details
  const handleDayCellClick = (table, date) => {
    const schedule = getScheduleForDay(table.id, date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase());
    if (!schedule) return; // No schedule for this day
    
    setSelectedDayDetail({
      table,
      date,
      schedule
    });
    setShowDayDetailModal(true);
  };

  // Open reservation details modal
  const handleViewReservation = async (reservation) => {
    setSelectedReservation(reservation);
    setShowReservationModal(true);
    setIsEditMode(false);
    setModalDataReady(false);

    try {
      // Fetch table settings to get service-category mappings
      const settingsResponse = await axios.get(`${API_BASE_URL}/api/table-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const settings = settingsResponse.data?.settings || {};
      const mappings = settings.service_category_mappings || {};
      
      // Get service IDs with non-empty mappings
      const serviceIdsWithMappings = Object.keys(mappings).filter(serviceId => {
        const categories = mappings[serviceId];
        return Array.isArray(categories) && categories.length > 0;
      });

      // Fetch services
      let servicesWithMappings = [];
      if (serviceIdsWithMappings.length > 0) {
        const allServiceCategoriesResponse = await axios.get(`${API_BASE_URL}/api/appointment-service-menu-categories`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (allServiceCategoriesResponse.data?.success) {
          const serviceCategories = allServiceCategoriesResponse.data.menu_categories || [];
          const servicePromises = serviceCategories.map(category =>
            axios.get(`${API_BASE_URL}/api/appointment-service-categories/${category.id}/services`, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => ({ data: { services: [] } }))
          );

          const servicesResponses = await Promise.all(servicePromises);
          const allServices = servicesResponses.flatMap(res => res.data?.services || []);
          servicesWithMappings = allServices.filter(service => serviceIdsWithMappings.includes(service.id));
        }
      }

      setModalServices(servicesWithMappings);
      setModalTables(tables);

      const serviceId = reservation.selected_service_id || reservation.selected_service?.id;
      
      // Get full service object from modalServices to include options
      const fullServiceObj = servicesWithMappings.find(s => s.id === serviceId);
      const serviceWithOptions = fullServiceObj ? {
        ...fullServiceObj,
        selectedOptions: reservation.selected_service?.selectedOptions || {}
      } : reservation.selected_service;
      
      // Filter tables based on service
      const linkedCategories = mappings[serviceId] || [];
      const categoryArray = Array.isArray(linkedCategories) ? linkedCategories : [];
      let relevantTables = tables;
      if (categoryArray.length > 0) {
        relevantTables = tables.filter(table => categoryArray.includes(table.category_id));
      }
      if (reservation.assigned_table_id) {
        const currentTable = tables.find(s => s.id === reservation.assigned_table_id);
        if (currentTable && !relevantTables.find(s => s.id === reservation.assigned_table_id)) {
          relevantTables = [currentTable, ...relevantTables];
        }
      }
      setFilteredTables(relevantTables);

      console.log('=== SERVICE WITH OPTIONS DEBUG ===');
      console.log('Service ID:', serviceId);
      console.log('Full Service Object:', fullServiceObj);
      console.log('Has Options:', fullServiceObj?.has_options);
      console.log('Options Array:', fullServiceObj?.options);
      console.log('Options Length:', fullServiceObj?.options?.length);
      console.log('Service With Options:', serviceWithOptions);
      console.log('Modal Services:', modalServices.map(s => ({ id: s.id, name: s.service_name || s.name, hasOptions: s.options?.length || 0 })));
      
      // Find the category of the assigned table
      const assignedTable = tables.find(t => t.id === reservation.assigned_table_id);
      const categoryId = assignedTable?.category_id || '';
      
      setEditForm({
        dropoff_date: reservation.dropoff_date,
        dropoff_time: reservation.dropoff_time,
        pickup_date: reservation.pickup_date,
        pickup_time: reservation.pickup_time,
        category_id: categoryId,
        assigned_table_id: reservation.assigned_table_id,
        selected_service_id: serviceId,
        selected_service: serviceWithOptions,
        status: reservation.status,
        form_details: reservation.form_details || {},
        room_details: reservation.room_details || {},
        special_instructions: reservation.special_instructions || {}
      });

      setModalDataReady(true);
    } catch (error) {
      console.error('Error loading reservation data:', error);
      setModalDataReady(true);
    }
  };

  const handleErrorModalClose = () => {
    setShowErrorModal(false);
    setErrorMessage('');
    
    // Reset form to original values
    const serviceId = selectedReservation.selected_service?.id;
    const fullServiceObj = modalServices.find(s => s.id === serviceId);
    const serviceWithOptions = fullServiceObj ? {
      ...fullServiceObj,
      selectedOptions: selectedReservation.selected_service?.selectedOptions || {}
    } : selectedReservation.selected_service;
    
    setEditForm({
      dropoff_date: selectedReservation.dropoff_date,
      dropoff_time: selectedReservation.dropoff_time,
      pickup_date: selectedReservation.pickup_date,
      pickup_time: selectedReservation.pickup_time,
      assigned_table_id: selectedReservation.assigned_table_id,
      selected_service_id: serviceId,
      selected_service: serviceWithOptions,
      status: selectedReservation.status
    });
  };

  const calculateAvailableSlots = (tableId, date, startTimeSlot) => {
    const requestedDuration = settings?.reservation_duration || 60;
    const slotsNeeded = Math.ceil(requestedDuration / 15);
    const allTimeSlots = generateTimeSlots();
    const startIndex = allTimeSlots.indexOf(startTimeSlot);
    
    if (startIndex === -1) return { availableSlots: 0, duration: 0 };
    
    // Check consecutive available slots
    let availableCount = 0;
    for (let i = 0; i < slotsNeeded && (startIndex + i) < allTimeSlots.length; i++) {
      const timeSlot = allTimeSlots[startIndex + i];
      const reservation = isTimeSlotBooked(tableId, date, timeSlot);
      
      if (reservation !== null) {
        // Found a booked/blocked slot, stop counting
        break;
      }
      availableCount++;
    }
    
    return {
      availableSlots: availableCount,
      duration: availableCount * 15,
      requestedSlots: slotsNeeded,
      requestedDuration: requestedDuration
    };
  };

  const handleBlockSlotClick = (table, date, timeSlot) => {
    const availability = calculateAvailableSlots(table.id, date, timeSlot);
    
    // If no slots available, show error
    if (availability.availableSlots === 0) {
      showMessage('error', 'No consecutive slots available to block from this time');
      return;
    }
    
    setSlotToBlock({
      table,
      date,
      timeSlot,
      duration: availability.duration,
      requestedDuration: availability.requestedDuration,
      availableSlots: availability.availableSlots,
      requestedSlots: availability.requestedSlots,
      isPartialBlock: availability.availableSlots < availability.requestedSlots
    });
    setShowBlockSlotModal(true);
  };

  const confirmBlockSlot = async () => {
    if (!slotToBlock) return;
    
    try {
      setLoading(true);
      
      // Create a blocked reservation
      const blockData = {
        reservation_type: 'table',
        dropoff_date: slotToBlock.date.toISOString().split('T')[0],
        dropoff_time: slotToBlock.timeSlot,
        guest_count: 0,
        duration_minutes: slotToBlock.duration,
        assigned_table_id: slotToBlock.table.id,
        assigned_table_name: slotToBlock.table.table_name,
        status: 'blocked',
        form_details: {
          blocked_by_admin: true,
          reason: 'Admin blocked'
        }
      };
      
      const response = await axios.post(`${API_BASE_URL}/api/table-reservations`, blockData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        showMessage('success', `Slot ${slotToBlock.timeSlot} blocked successfully for ${slotToBlock.duration} minutes`);
        fetchReservations();
        setShowBlockSlotModal(false);
        setSlotToBlock(null);
      }
    } catch (error) {
      console.error('Error blocking slot:', error);
      showMessage('error', error.response?.data?.detail || 'Error blocking slot');
    } finally {
      setLoading(false);
    }
  };

  const confirmUnblockSlot = async () => {
    if (!slotToBlock || !slotToBlock.reservationId) return;
    
    try {
      setLoading(true);
      
      const response = await axios.delete(`${API_BASE_URL}/api/table-reservations/${slotToBlock.reservationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        showMessage('success', `Slot ${slotToBlock.timeSlot} unblocked successfully`);
        fetchReservations();
        setShowBlockSlotModal(false);
        setSlotToBlock(null);
      }
    } catch (error) {
      console.error('Error unblocking slot:', error);
      showMessage('error', error.response?.data?.detail || 'Error unblocking slot');
    } finally {
      setLoading(false);
    }
  };

  const handleOptionChange = (optionName, value) => {
    setSelectedServiceOptions({
      ...selectedServiceOptions,
      [optionName]: value
    });
  };

  const handleOptionsConfirm = () => {
    // Save options to edit form
    setEditForm({
      ...editForm,
      selected_service: {
        ...editForm.selected_service,
        selectedOptions: selectedServiceOptions
      }
    });
    setShowOptionsModal(false);
  };

  const handleSaveReservationEdit = async () => {
    try {
      setIsUpdating(true); // Show loading modal
      
      // Check if service or dates/times changed
      const serviceChanged = editForm.selected_service_id !== selectedReservation.selected_service?.id;
      const datesChanged = 
        editForm.dropoff_date !== selectedReservation.dropoff_date ||
        editForm.dropoff_time !== selectedReservation.dropoff_time ||
        editForm.pickup_date !== selectedReservation.pickup_date ||
        editForm.pickup_time !== selectedReservation.pickup_time;

      let assignedTableId = editForm.assigned_table_id;

      // If service or dates changed, check availability and find a new table
      if (serviceChanged || datesChanged) {
        console.log('Service or dates changed, checking availability...');
        
        // Call check-availability API
        const availabilityResponse = await axios.post(
          `${API_BASE_URL}/api/table/check-availability`,
          {
            check_in_date: editForm.dropoff_date,
            check_in_time: editForm.dropoff_time,
            check_out_date: editForm.pickup_date,
            check_out_time: editForm.pickup_time,
            exclude_reservation_id: selectedReservation.id // Exclude current reservation from availability check
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        console.log('Availability response:', availabilityResponse.data);

        // Find the selected service in available services
        const availableServices = availabilityResponse.data.available_services || [];
        const selectedService = availableServices.find(s => s.id === editForm.selected_service_id);

        if (!selectedService) {
          setIsUpdating(false); // Hide loading modal
          const serviceName = editForm.selected_service?.service_name || editForm.selected_service?.name || 'this service';
          
          // Show custom error modal
          setErrorMessage(`${serviceName} is not available for the selected dates and times. Please select different dates or times.`);
          setShowErrorModal(true);
          return;
        }

        // Check if there's an available table
        if (!selectedService.assigned_table_id) {
          setIsUpdating(false); // Hide loading modal
          const serviceName = editForm.selected_service?.service_name || editForm.selected_service?.name || 'this service';
          
          // Show custom error modal
          setErrorMessage(`No table available for ${serviceName} for the selected dates and times. Please select different dates or times.`);
          setShowErrorModal(true);
          return;
        }

        // Use the assigned table from availability check
        assignedTableId = selectedService.assigned_table_id;
        console.log('New table assigned:', assignedTableId);
      }

      // Update the reservation
      await axios.patch(
        `${API_BASE_URL}/api/table-reservations/${selectedReservation.id}`,
        {
          dropoff_date: editForm.dropoff_date,
          dropoff_time: editForm.dropoff_time,
          pickup_date: editForm.pickup_date,
          pickup_time: editForm.pickup_time,
          assigned_table_id: assignedTableId,
          selected_service_id: editForm.selected_service_id,
          status: editForm.status
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showMessage('success', 'Reservation updated successfully');
      setIsEditMode(false);
      fetchReservations(); // This will refresh the calendar and free up old table
      setShowReservationModal(false);
      setIsUpdating(false); // Hide loading modal
    } catch (error) {
      console.error('Error updating reservation:', error);
      showMessage('error', error.response?.data?.message || 'Failed to update reservation');
      setIsUpdating(false); // Hide loading modal on error
    }
  };

  const handleServiceChange = (serviceId) => {
    const selectedServiceObj = modalServices.find(s => s.id === serviceId);
    
    // Update the service first
    setEditForm({
      ...editForm,
      selected_service_id: serviceId,
      selected_service: selectedServiceObj,
      assigned_table_id: ''
    });
    
    // If service has options, show options modal
    if (selectedServiceObj?.has_options && selectedServiceObj?.options?.length > 0) {
      // Try to carry over previous selections
      const previousOptions = selectedReservation.selected_service?.selectedOptions || {};
      const newOptions = {};
      
      // Map previous selections to new service options where option names match
      selectedServiceObj.options.forEach(option => {
        const previousValue = previousOptions[option.option_name];
        if (previousValue !== undefined) {
          newOptions[option.option_name] = previousValue;
        }
      });
      
      setSelectedServiceOptions(newOptions);
      setShowOptionsModal(true);
    }
  };

  const fetchTableSchedules = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/table-schedules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.schedules) {
        setTableSchedules(response.data.schedules);
      }
    } catch (error) {
      console.error('Error fetching table schedules:', error);
    }
  };

  const fetchBlockedDates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/table-blocked-dates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Blocked dates fetched:', response.data);
      if (response.data?.blocked_dates) {
        setBlockedDates(response.data.blocked_dates);
        console.log('Blocked dates state updated with', response.data.blocked_dates.length, 'entries');
      }
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
    }
  };

  const handleAddTable = async () => {
    if (!tableForm.table_name) {
      showMessage('error', 'Table name is required');
      return;
    }

    setLoading(true);
    try {
      let tableId;
      
      if (editingTable) {
        // Update existing table
        const updateData = {
          table_name: tableForm.table_name,
          capacity: tableForm.capacity || 2,
          description: tableForm.description,
          category_id: tableForm.category_id || null
        };
        
        await axios.put(`${API_BASE_URL}/api/tables/${editingTable.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Update default schedule settings for all schedules of this table
        const schedulesToUpdate = tableSchedules.filter(s => s.table_id === editingTable.id);
        const scheduleUpdateData = {
          start_time: tableForm.is_24_hours ? '00:00' : tableForm.start_time,
          end_time: tableForm.is_24_hours ? '23:59' : tableForm.end_time,
          slot_duration_minutes: tableForm.enable_slot_booking ? tableForm.slot_duration_minutes : 1440,
          breaks: tableForm.breaks || []
        };
        
        // Update each schedule with new default settings
        const updatePromises = schedulesToUpdate.map(schedule => 
          axios.put(`${API_BASE_URL}/api/table-schedules/${schedule.id}`, {
            ...schedule,
            ...scheduleUpdateData
          }, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(err => {
            console.error(`Error updating schedule ${schedule.id}:`, err);
            return null;
          })
        );
        
        await Promise.all(updatePromises);
        
        showMessage('success', 'Table and schedules updated successfully');
      } else {
        // Create new table
        const createData = {
          table_name: tableForm.table_name,
          capacity: tableForm.capacity || 2,
          description: tableForm.description,
          category_id: tableForm.category_id || null
        };
        
        const response = await axios.post(`${API_BASE_URL}/api/tables`, createData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        tableId = response.data.table?.id;
        
        // Create schedules for the duration
        if (tableId) {
          await createSchedulesForTable(tableId);
          
          // Save temporarily blocked dates for new table
          if (tempBlockedDates.length > 0) {
            try {
              await axios.post(
                `${API_BASE_URL}/api/table-blocked-dates/bulk`,
                {
                  table_id: tableId,
                  dates: tempBlockedDates,
                  is_blocked: true,
                  reason: 'Pre-blocked during table creation'
                },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              setTempBlockedDates([]);
            } catch (error) {
              console.error('Error saving blocked dates:', error);
            }
          }
        }
        
        showMessage('success', 'Table and schedules created successfully');
      }
      
      fetchTables();
      fetchTableSchedules();
      fetchBlockedDates();
      setShowTableModal(false);
      setTableForm({ 
        table_name: '', 
        capacity: 2,
        description: '',
        category_id: '',
        schedule_duration_months: 1,
        is_24_hours: false,
        start_time: '09:00',
        end_time: '17:00',
        slot_duration_minutes: 30,
        enable_slot_booking: false,
        bulk_start_date: '',
        bulk_end_date: ''
      });
      setEditingTable(null);
    } catch (error) {
      console.error('Error saving table:', error);
      showMessage('error', 'Failed to save table');
    } finally {
      setLoading(false);
    }
  };

  const createSchedulesForTable = async (tableId) => {
    const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const scheduleData = {
      start_time: tableForm.is_24_hours ? '00:00' : tableForm.start_time,
      end_time: tableForm.is_24_hours ? '23:59' : tableForm.end_time,
      slot_duration_minutes: tableForm.enable_slot_booking ? tableForm.slot_duration_minutes : 1440, // 1440 min = 24 hours (full day)
      is_enabled: true,
      breaks: tableForm.breaks || []
    };

    // Create schedule for each day of the week
    const createPromises = daysOfWeek.map(day => {
      return axios.post(`${API_BASE_URL}/api/table-schedules`, {
        table_id: tableId,
        day_of_week: day,
        ...scheduleData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(err => {
        console.error(`Error creating schedule for ${day}:`, err);
        return null;
      });
    });

    await Promise.all(createPromises);
  };

  const handleDeleteTable = async () => {
    if (!tableToDelete) return;

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/tables/${tableToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showMessage('success', 'Table deleted successfully');
      fetchTables();
      fetchTableSchedules();
      setShowDeleteTableModal(false);
      setTableToDelete(null);
    } catch (error) {
      console.error('Error deleting table:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete table';
      showMessage('error', errorMsg);
      setShowDeleteTableModal(false);
      setTableToDelete(null);
    } finally {
      setLoading(false);
    }
  };

  // Table Category Management Functions
  const handleSaveCategory = async () => {
    if (!categoryForm.category_name.trim()) {
      showMessage('error', 'Category name is required');
      return;
    }

    setLoading(true);
    try {
      if (editingCategory) {
        await axios.put(`${API_BASE_URL}/api/table-categories/${editingCategory.id}`, categoryForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showMessage('success', 'Category updated successfully');
        setEditingCategory(null);
      } else {
        await axios.post(`${API_BASE_URL}/api/table-categories`, categoryForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showMessage('success', 'Category added successfully');
      }
      
      // Reset form but keep modal open to allow adding more categories
      setCategoryForm({ category_name: '', description: '' });
      fetchTableCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to save category';
      showMessage('error', errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/table-categories/${categoryToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showMessage('success', 'Category deleted successfully');
      fetchTableCategories();
      setShowDeleteCategoryModal(false);
      setCategoryToDelete(null);
    } catch (error) {
      console.error('Error deleting category:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete category';
      showMessage('error', errorMsg);
      setShowDeleteCategoryModal(false);
      setCategoryToDelete(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    if (!selectedTableId || !editingSchedule?.day_of_week) {
      showMessage('error', 'Table and day are required');
      return;
    }

    setLoading(true);
    try {
      if (editingSchedule.id) {
        // Update existing schedule
        await axios.put(`${API_BASE_URL}/api/table-schedules/${editingSchedule.id}`, scheduleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showMessage('success', 'Schedule updated successfully');
      } else {
        // Create new schedule
        const newSchedule = {
          table_id: selectedTableId,
          day_of_week: editingSchedule.day_of_week,
          ...scheduleForm
        };
        
        await axios.post(`${API_BASE_URL}/api/table-schedules`, newSchedule, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showMessage('success', 'Schedule created successfully');
      }
      
      fetchTableSchedules();
      setShowScheduleModal(false);
      setScheduleForm({ start_time: '09:00', end_time: '17:00', slot_duration_minutes: 30, is_enabled: true, breaks: [] });
      setEditingSchedule(null);
      setSelectedTableId(null);
    } catch (error) {
      console.error('Error saving schedule:', error);
      showMessage('error', error.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setLoading(false);
    }
  };

  // Bulk calendar update handler
  const handleBulkCalendarUpdate = async (action) => {
    if (!tableForm.bulk_start_date || !tableForm.bulk_end_date) {
      showMessage('error', 'Please select both start and end dates');
      return;
    }

    const startDate = new Date(tableForm.bulk_start_date);
    const endDate = new Date(tableForm.bulk_end_date);

    if (endDate < startDate) {
      showMessage('error', 'End date must be after start date');
      return;
    }

    const dates = [];
    const currentDate = new Date(startDate);
    
    // Generate all dates in the range
    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // If editing an existing table, save immediately
    if (editingTable?.id) {
      setLoading(true);
      try {
        // Call the bulk update endpoint
        const isBlocked = action === 'block';
        const response = await axios.post(
          `${API_BASE_URL}/api/table-blocked-dates/bulk`,
          {
            table_id: editingTable.id,
            dates: dates,
            is_blocked: isBlocked,
            reason: isBlocked ? 'Blocked by admin' : 'Scheduled/Available for booking'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showMessage('success', `Successfully ${action === 'block' ? 'blocked' : 'scheduled'} ${response.data.updated_count} dates`);
      
      // Clear the date inputs
      setTableForm({ 
        ...tableForm, 
        bulk_start_date: '', 
        bulk_end_date: '' 
      });
      
      // Refresh data
      fetchTables();
      fetchTableSchedules();
      fetchBlockedDates();
    } catch (error) {
      console.error('Error in bulk update:', error);
      showMessage('error', error.response?.data?.detail || 'Failed to update dates');
    } finally {
      setLoading(false);
    }
    } else {
      // For new table, store dates temporarily
      if (action === 'block') {
        setTempBlockedDates(dates);
        showMessage('success', `${dates.length} date(s) will be blocked when table is created`);
        
        // Clear the date inputs
        setTableForm({ 
          ...tableForm, 
          bulk_start_date: '', 
          bulk_end_date: '' 
        });
      }
    }
  };

  // Week navigation functions
  const getWeekDates = () => {
    const dates = [];
    const start = new Date(currentWeekStart);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const navigateWeek = (direction) => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + (direction * 7));
    setCurrentWeekStart(newStart);
  };

  const formatDateForDisplay = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', weekday: 'short' });
  };

  const formatDateForAPI = (date) => {
    // Use local date to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getScheduleForDay = (tableId, dayOfWeek) => {
    return tableSchedules.find(s => s.table_id === tableId && s.day_of_week === dayOfWeek);
  };

  const getDateStatus = (tableId, date) => {
    const dateStr = formatDateForAPI(date);
    const dateEntry = blockedDates.find(
      bd => bd.table_id === tableId && bd.date === dateStr
    );
    if (dateEntry) {
      console.log(`Found date status for ${dateStr}:`, dateEntry);
    }
    return dateEntry; // Returns the entry or undefined
  };

  const isDateBlocked = (tableId, date) => {
    const dateEntry = getDateStatus(tableId, date);
    return dateEntry?.is_blocked === true;
  };

  const isDateScheduled = (tableId, date) => {
    const dateEntry = getDateStatus(tableId, date);
    return dateEntry?.is_blocked === false;
  };

  const toggleDateBlock = async (tableId, date) => {
    const dateStr = formatDateForAPI(date);
    const dateEntry = getDateStatus(tableId, date);
    const currentlyBlocked = dateEntry?.is_blocked === true;
    const currentlyScheduled = dateEntry?.is_blocked === false;
    
    try {
      // Toggle logic: blocked -> scheduled, scheduled/no-entry -> blocked
      const newIsBlocked = !currentlyBlocked;
      
      await axios.post(
        `${API_BASE_URL}/api/table-blocked-dates/bulk`,
        {
          table_id: tableId,
          dates: [dateStr],
          is_blocked: newIsBlocked,
          reason: newIsBlocked ? 'Blocked by admin' : 'Scheduled/Available for booking'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchBlockedDates();
      showMessage('success', `Date ${newIsBlocked ? 'blocked' : 'scheduled'} successfully`);
    } catch (error) {
      console.error('Error toggling date block:', error);
      showMessage('error', 'Failed to update date');
    }
  };

  const fetchFormFields = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/table-form-fields`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.fields) {
        // Convert fields object to array and sort by order
        const fieldsArray = Object.values(response.data.fields).sort((a, b) => (a.order || 0) - (b.order || 0));
        // For table reservation, all fields go into formFields
        setFormFields(fieldsArray);
        // Keep other sections empty for table reservations
        setTourFields([]);
        setRoomFields([]);
        setSpecialInstructionFields([]);
      }
    } catch (error) {
      console.error('Error fetching form fields:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/table-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.settings) {
        const settingsData = response.data.settings;
        setSettings(settingsData);
        setRoomDetailsEnabled(settingsData.room_details_enabled !== false);
        setSpecialInstructionsEnabled(settingsData.special_instructions_enabled !== false);
        // Load selected service categories
        if (settingsData.selected_service_categories) {
          setSelectedCategories(settingsData.selected_service_categories);
        }
        // Load service-category mappings
        if (settingsData.service_category_mappings) {
          setServiceCategoryMappings(settingsData.service_category_mappings);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchDocumentFields = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/table-document-fields`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.fields) {
        setDocumentFields(response.data.fields);
      }
    } catch (error) {
      console.error('Error fetching document fields:', error);
    }
  };

  const fetchServiceCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-service-menu-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.menu_categories) {
        setServiceCategories(response.data.menu_categories);
        // Fetch services for each category
        response.data.menu_categories.forEach(cat => {
          fetchCategoryServices(cat.id);
        });
      }
    } catch (error) {
      console.error('Error fetching service categories:', error);
    }
  };

  const fetchCategoryServices = async (menuCategoryId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-services?menu_category_id=${menuCategoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.services) {
        setCategoryServices(prev => ({ ...prev, [menuCategoryId]: response.data.services }));
      }
    } catch (error) {
      console.error('Error fetching category services:', error);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/payment-gateways`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.success && response.data.gateways) {
        const activeModes = [];
        response.data.gateways.forEach(gateway => {
          if (gateway.enabled) {
            const label = gateway.gateway.charAt(0).toUpperCase() + gateway.gateway.slice(1);
            activeModes.push({ 
              value: gateway.gateway, 
              label: label 
            });
          }
        });
        setAvailablePaymentModes(activeModes);
      }
    } catch (error) {
      console.error('Error fetching payment gateways:', error);
    }
  };

  const handleAddField = () => {
    if (!fieldForm.field_name) {
      showMessage('error', 'Field name is required');
      return;
    }

    const currentFields = selectedSection === 'form' ? formFields : 
                          selectedSection === 'rooms' ? roomFields : specialInstructionFields;
    
    const newField = {
      id: editingField ? editingField.id : `field_${Date.now()}`,
      ...fieldForm,
      section: selectedSection,
      order: editingField ? editingField.order : currentFields.length,
      is_system: false
    };

    if (editingField) {
      // Update existing field
      if (selectedSection === 'form') {
        setFormFields(formFields.map(f => f.id === editingField.id ? newField : f));
      } else if (selectedSection === 'rooms') {
        setRoomFields(roomFields.map(f => f.id === editingField.id ? newField : f));
      } else {
        setSpecialInstructionFields(specialInstructionFields.map(f => f.id === editingField.id ? newField : f));
      }
      showMessage('success', 'Field updated successfully');
    } else {
      // Add new field
      if (selectedSection === 'form') {
        setFormFields([...formFields, newField]);
      } else if (selectedSection === 'rooms') {
        setRoomFields([...roomFields, newField]);
      } else {
        setSpecialInstructionFields([...specialInstructionFields, newField]);
      }
      showMessage('success', 'Field added successfully');
    }

    resetFieldForm();
  };

  const handleEditField = (field) => {
    setEditingField(field);
    setFieldForm({
      field_name: field.field_name,
      field_type: field.field_type,
      required: field.required,
      placeholder: field.placeholder || '',
      description: field.description || '',
      options: field.options || ''
    });
    setShowAddFieldModal(true);
  };

  const handleDeleteField = (fieldId) => {
    // Find the field to check if it's a system field
    const allFields = selectedSection === 'form' ? formFields : 
                      selectedSection === 'rooms' ? roomFields : specialInstructionFields;
    const field = allFields.find(f => f.id === fieldId);
    
    // Prevent deletion of system fields (First Name and Last Name)
    if (field?.is_system) {
      showMessage('error', 'System fields (First Name, Last Name) cannot be deleted as they are mandatory.');
      return;
    }

    // Show custom confirmation modal
    setFieldToDelete({ fieldId, fieldName: field?.field_name });
    setShowDeleteFieldModal(true);
  };

  const confirmDeleteField = () => {
    if (!fieldToDelete) return;

    if (selectedSection === 'form') {
      setFormFields(formFields.filter(f => f.id !== fieldToDelete.fieldId));
    } else if (selectedSection === 'rooms') {
      setRoomFields(roomFields.filter(f => f.id !== fieldToDelete.fieldId));
    } else {
      setSpecialInstructionFields(specialInstructionFields.filter(f => f.id !== fieldToDelete.fieldId));
    }
    
    showMessage('success', 'Field deleted successfully');
    setShowDeleteFieldModal(false);
    setFieldToDelete(null);
  };

  const handleMoveField = (fieldId, direction) => {
    const currentFields = selectedSection === 'form' ? formFields : 
                          selectedSection === 'rooms' ? roomFields : specialInstructionFields;
    const index = currentFields.findIndex(f => f.id === fieldId);
    if (index === -1) return;

    const newFields = [...currentFields];
    if (direction === 'up' && index > 0) {
      [newFields[index], newFields[index - 1]] = [newFields[index - 1], newFields[index]];
    } else if (direction === 'down' && index < newFields.length - 1) {
      [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
    }

    // Update order
    newFields.forEach((field, idx) => {
      field.order = idx;
    });

    if (selectedSection === 'form') {
      setFormFields(newFields);
    } else if (selectedSection === 'rooms') {
      setRoomFields(newFields);
    } else {
      setSpecialInstructionFields(newFields);
    }
  };

  const resetFieldForm = () => {
    setFieldForm({
      field_name: '',
      field_type: 'text',
      required: false,
      placeholder: '',
      description: '',
      options: ''
    });
    setEditingField(null);
    setShowAddFieldModal(false);
  };

  const handleAddDocument = () => {
    if (!documentForm.title) {
      showMessage('error', 'Document title is required');
      return;
    }

    const newDocument = {
      id: `doc_${Date.now()}`,
      ...documentForm,
      order: documentFields.length
    };

    setDocumentFields([...documentFields, newDocument]);
    setDocumentForm({ title: '', description: '' });
    setShowAddDocumentModal(false);
    showMessage('success', 'Document field added successfully');
  };

  const handleDeleteDocument = (docId) => {
    if (!window.confirm('Are you sure you want to delete this document field?')) return;
    setDocumentFields(documentFields.filter(d => d.id !== docId));
    showMessage('success', 'Document field deleted successfully');
  };

  const handleSaveAll = async () => {
    setLoading(true);
    try {
      // Save settings with all configurations
      const settingsData = {
        ...settings,
        room_details_enabled: roomDetailsEnabled,
        special_instructions_enabled: specialInstructionsEnabled,
        selected_service_categories: selectedCategories,  // Save selected categories
        service_category_mappings: serviceCategoryMappings  // Save service-category mappings
      };

      await axios.put(`${API_BASE_URL}/api/table-settings`, settingsData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Ensure system fields (First Name, Last Name) are always included for form section
      const systemFormFields = [
        {
          id: 'default_first_name',
          field_name: 'First Name',
          field_type: 'text',
          required: true,
          is_system: true,
          section: 'form',
          order: 0
        },
        {
          id: 'default_last_name',
          field_name: 'Last Name',
          field_type: 'text',
          required: true,
          is_system: true,
          section: 'form',
          order: 1
        }
      ];

      // Ensure system fields for tour section
      const systemTourFields = [
        {
          id: 'default_tour_first_name',
          field_name: 'First Name',
          field_type: 'text',
          required: true,
          is_system: true,
          section: 'tour',
          order: 0
        },
        {
          id: 'default_tour_last_name',
          field_name: 'Last Name',
          field_type: 'text',
          required: true,
          is_system: true,
          section: 'tour',
          order: 1
        }
      ];

      // Merge system fields with user-created fields, ensuring system fields come first
      const userFormFields = formFields.filter(f => !f.is_system);
      const userTourFields = tourFields.filter(f => !f.is_system);
      const fieldsToSave = [...systemFormFields, ...userFormFields, ...systemTourFields, ...userTourFields, ...roomFields, ...specialInstructionFields];

      // Delete all existing form fields first
      const existingFields = await axios.get(`${API_BASE_URL}/api/table-form-fields`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (existingFields.data?.fields) {
        const fieldIds = Object.values(existingFields.data.fields).map(f => f.id);
        await Promise.all(
          fieldIds.map(id => 
            axios.delete(`${API_BASE_URL}/api/table-form-fields/${id}`, {
              headers: { Authorization: `Bearer ${token}` }
            })
          )
        );
      }

      // Save each form field individually
      for (const field of fieldsToSave) {
        const fieldData = {
          section: field.section || 'form',
          field_name: field.field_name,
          field_type: field.field_type,
          required: field.required || false,
          placeholder: field.placeholder || '',
          description: field.description || '',
          options: field.options ? (Array.isArray(field.options) ? field.options : field.options.split(',').map(o => o.trim())) : []
        };
        
        await axios.post(`${API_BASE_URL}/api/table-form-fields`, fieldData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      // Document fields save not implemented yet
      // TODO: Implement document fields save if needed

      showMessage('success', 'All settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save settings';
      showMessage('error', `Failed to save: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllBookings = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/api/table/bookings/delete-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showMessage('success', 'All table bookings deleted successfully');
      setShowDeleteAllBookingsModal(false);
      fetchReservations(); // Refresh the calendar
    } catch (error) {
      console.error('Error deleting bookings:', error);
      showMessage('error', 'Failed to delete bookings');
      setShowDeleteAllBookingsModal(false);
    }
  };

  const handleDeleteAllTours = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/api/table/tour-schedules/delete-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showMessage('success', 'All tour schedules deleted successfully');
      setShowDeleteAllToursModal(false);
      fetchReservations(); // Refresh the calendar
    } catch (error) {
      console.error('Error deleting tour schedules:', error);
      showMessage('error', 'Failed to delete tour schedules');
      setShowDeleteAllToursModal(false);
    }
  };

  const handleRefreshSchedules = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/tables/refresh-schedules`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        showMessage('success', `Schedules refreshed successfully! Updated ${response.data.updated_count || 0} table schedules based on business hours.`);
        fetchReservations(); // Refresh the calendar
      }
    } catch (error) {
      console.error('Error refreshing schedules:', error);
      showMessage('error', error.response?.data?.detail || 'Failed to refresh schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleResetCounter = async () => {
    if (!window.confirm('Are you sure you want to reset the table number counter?')) return;
    
    try {
      await axios.post(`${API_BASE_URL}/api/table/reset-counter`, {
        start_number: settings.table_number_start
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showMessage('success', 'Table number counter reset successfully');
    } catch (error) {
      console.error('Error resetting counter:', error);
      showMessage('error', 'Failed to reset counter');
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const getCurrentFields = () => {
    if (selectedSection === 'form') return formFields;
    if (selectedSection === 'rooms') return roomFields;
    return specialInstructionFields;
  };

  const isSectionEnabled = () => {
    if (selectedSection === 'rooms') return roomDetailsEnabled;
    if (selectedSection === 'special_instructions') return specialInstructionsEnabled;
    return true; // form and tour are always enabled
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-2 sm:px-4 lg:px-8 py-4">
        {/* Header */}
        <div className="bg-white rounded-t-xl shadow-lg mb-6 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Manage Table
            </h2>
          </div>

          {/* Tabs */}
          <div className="flex table-x-1 bg-gray-100 p-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center table-x-2 px-3 py-2 text-sm font-medium rounded transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Message Display */}
        {message.text && (
          <div className={`mb-4 p-4 rounded-lg flex items-center table-x-2 ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            <span>{message.text}</span>
          </div>
        )}

        {/* Tab Content */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Manage Tables Tab */}
          {activeTab === 'manage-tables' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-base font-semibold text-gray-900">Manage Tables</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setEditingCategory(null);
                      setCategoryForm({ category_name: '', description: '' });
                      setShowCategoryModal(true);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    <Settings className="h-3.5 w-3.5" />
                    Manage Categories
                  </button>
                  <button
                    onClick={handleRefreshSchedules}
                    disabled={loading}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                        Refreshing...
                      </>
                    ) : (
                      <>
                        <Calendar className="h-3.5 w-3.5" />
                        Refresh Schedules
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setEditingTable(null);
                      setTableForm({ 
                        table_name: '', 
                        capacity: 2,
                        description: '',
                        category_id: '',
                        schedule_duration_months: 1,
                        is_24_hours: false,
                        start_time: '09:00',
                        end_time: '17:00',
                        slot_duration_minutes: 30,
                        enable_slot_booking: false,
                        bulk_start_date: '',
                        bulk_end_date: ''
                      });
                      setShowTableModal(true);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-white rounded-md hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Table
                  </button>
                </div>
              </div>

              {/* Filter and Search Section */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search tables by name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="w-64">
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Categories</option>
                    {tableCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.category_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {tables.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <Building className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-2">No table tables found</p>
                  <p className="text-sm text-gray-500 mb-4">
                    Create tables to manage table availability
                  </p>
                  <button
                    onClick={() => {
                      setEditingTable(null);
                      setTableForm({ 
                        table_name: '', 
                        capacity: 2,
                        description: '',
                        schedule_duration_months: 1,
                        is_24_hours: false,
                        start_time: '09:00',
                        end_time: '17:00',
                        slot_duration_minutes: 30,
                        enable_slot_booking: false,
                        bulk_start_date: '',
                        bulk_end_date: ''
                      });
                      setShowTableModal(true);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white rounded-lg"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Plus className="h-4 w-4" />
                    Add Your First Table
                  </button>
                </div>
              ) : (
                <div>
                  {/* Week Navigation */}
                  <div className="mb-4 flex items-center justify-between">
                    <button
                      onClick={() => navigateWeek(-1)}
                      className="flex items-center table-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span>Previous Week</span>
                    </button>
                    
                    <div className="text-center">
                      <h3 className="text-sm font-medium text-gray-900">
                        Week of {formatDateForDisplay(currentWeekStart)}
                      </h3>
                      <p className="text-xs text-gray-600">
                        {formatDateForAPI(currentWeekStart)} to {formatDateForAPI(getWeekDates()[6])}
                      </p>
                    </div>
                    
                    <button
                      onClick={() => navigateWeek(1)}
                      className="flex items-center table-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                    >
                      <span>Next Week</span>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <div className="min-w-full" style={{ minWidth: '800px' }}>
                      {/* Week Header */}
                      <div className="grid grid-cols-8 bg-gray-50 border-b border-gray-200">
                        <div className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Table
                        </div>
                        {getWeekDates().map((date, index) => (
                          <div key={index} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <div>{formatDateForDisplay(date)}</div>
                            <div className="text-gray-400 font-normal text-xs">
                              {formatDateForAPI(date)}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Schedule Rows */}
                      <div className="bg-white divide-y divide-gray-200">
                        {tables
                          .filter(table => {
                            // Filter by search query
                            const matchesSearch = searchQuery === '' || 
                              table.table_name.toLowerCase().includes(searchQuery.toLowerCase());
                            
                            // Filter by category
                            const matchesCategory = categoryFilter === '' || 
                              table.category_id === categoryFilter;
                            
                            return matchesSearch && matchesCategory;
                          })
                          .map((table) => (
                          <div key={table.id} className="grid grid-cols-8">
                            {/* Table Info */}
                            <div className="px-4 py-6 border-r border-gray-200">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {table.table_name}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  Seats: {table.capacity || 2}
                                  {table.category_name && ` • ${table.category_name}`}
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                  <label className="flex items-center gap-1">
                                    <input
                                      type="checkbox"
                                      checked={table.is_enabled !== false}
                                      onChange={async (e) => {
                                        try {
                                          await axios.put(
                                            `${API_BASE_URL}/api/tables/${table.id}`,
                                            {
                                              table_name: table.table_name,
                                              description: table.description,
                                              is_enabled: e.target.checked
                                            },
                                            { headers: { Authorization: `Bearer ${token}` } }
                                          );
                                          fetchTables();
                                          showMessage('success', `Table ${e.target.checked ? 'enabled' : 'disabled'} successfully`);
                                        } catch (error) {
                                          showMessage('error', 'Failed to update table');
                                        }
                                      }}
                                      className="rounded text-xs"
                                    />
                                    <span className="text-xs text-gray-600">On</span>
                                  </label>
                                  <button
                                    onClick={() => {
                                      // Get the first schedule for this table to read default settings
                                      const firstSchedule = tableSchedules.find(s => s.table_id === table.id);
                                      const is24Hours = firstSchedule?.start_time === '00:00' && firstSchedule?.end_time === '23:59';
                                      const enableSlotBooking = firstSchedule?.slot_duration_minutes < 1440;
                                      
                                      setEditingTable(table);
                                      setTableForm({
                                        table_name: table.table_name,
                                        capacity: table.capacity || 2,
                                        description: table.description || '',
                                        category_id: table.category_id || '',
                                        schedule_duration_months: 1,
                                        is_24_hours: is24Hours,
                                        start_time: firstSchedule?.start_time || '09:00',
                                        end_time: firstSchedule?.end_time || '17:00',
                                        slot_duration_minutes: firstSchedule?.slot_duration_minutes || 30,
                                        enable_slot_booking: enableSlotBooking,
                                        bulk_start_date: '',
                                        bulk_end_date: '',
                                        breaks: firstSchedule?.breaks || []
                                      });
                                      setShowTableModal(true);
                                    }}
                                    className="p-1 text-theme-blue hover:bg-blue-50 rounded"
                                    title="Edit table"
                                  >
                                    <Edit3 className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setTableToDelete(table);
                                      setShowDeleteTableModal(true);
                                    }}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                    title="Delete table"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Daily Schedule Cells */}
                            {getWeekDates().map((date, dateIndex) => {
                              const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                              const schedule = getScheduleForDay(table.id, dayOfWeek);
                              const isBlocked = isDateBlocked(table.id, date);
                              const isScheduled = isDateScheduled(table.id, date);
                              const reservationsOnDate = getReservationsForTableAndDate(table.id, date);
                              const hasReservation = reservationsOnDate.length > 0;
                              const hasMultipleReservations = reservationsOnDate.length > 1;
                              
                              // Determine reservation type for this date
                              const dateStr = formatDateForAPI(date);
                              let reservationInfo = null;
                              
                              if (hasReservation && reservationsOnDate.length === 1) {
                                const reservation = reservationsOnDate[0];
                                const isSameDay = reservation.dropoff_date === reservation.pickup_date;
                                const isCheckInDay = dateStr === reservation.dropoff_date;
                                const isCheckOutDay = dateStr === reservation.pickup_date;
                                const isMiddleDay = !isCheckInDay && !isCheckOutDay;
                                
                                reservationInfo = {
                                  reservation,
                                  isSameDay,
                                  isCheckInDay,
                                  isCheckOutDay,
                                  isMiddleDay,
                                  // For same-day: reserved only during actual hours
                                  // For check-in day: reserved from check-in time to end of day (12am)
                                  // For check-out day: reserved from start of day (12am) to check-out time
                                  // For middle day: fully reserved
                                  isPartialDay: isSameDay || isCheckInDay || isCheckOutDay
                                };
                              }
                              
                              return (
                                <div 
                                  key={dateIndex} 
                                  className="px-2 py-4 border-r border-gray-200 h-[140px] cursor-pointer hover:bg-gray-50 transition-colors"
                                  onClick={() => handleDayCellClick(table, date)}
                                >
                                  {schedule ? (
                                    hasMultipleReservations ? (
                                      // Split cell for multiple reservations
                                      <div className="rounded-lg border border-dotted border-blue-400 h-full flex flex-col overflow-hidden">
                                        {/* First Reservation - Upper Half */}
                                        <div 
                                          className="flex-1 bg-blue-100 hover:bg-blue-200 cursor-pointer flex flex-col items-center justify-center border-b border-blue-300"
                                          onClick={() => handleViewReservation(reservationsOnDate[0])}
                                        >
                                          <div className="text-[10px] font-bold text-blue-900 mb-0.5">RESERVED</div>
                                          <div className="text-[9px] text-blue-700">
                                            {reservationsOnDate[0].dropoff_time} - {reservationsOnDate[0].pickup_time}
                                          </div>
                                        </div>
                                        
                                        {/* Second Reservation - Lower Half */}
                                        <div 
                                          className="flex-1 bg-purple-100 hover:bg-purple-200 cursor-pointer flex flex-col items-center justify-center"
                                          onClick={() => handleViewReservation(reservationsOnDate[1])}
                                        >
                                          <div className="text-[10px] font-bold text-purple-900 mb-0.5">RESERVED</div>
                                          <div className="text-[9px] text-purple-700">
                                            {reservationsOnDate[1].dropoff_time} - {reservationsOnDate[1].pickup_time}
                                          </div>
                                        </div>
                                      </div>
                                    ) : reservationInfo?.isPartialDay ? (
                                      // Partial day: Split cell based on same-day, check-in, or check-out
                                      <div className="rounded-lg border border-dotted border-blue-400 h-full flex flex-col overflow-hidden">
                                        {reservationInfo.isSameDay ? (
                                          // Same-day reservation: Show actual reserved hours + remaining available
                                          <>
                                            {/* Reserved During Booking Hours - Upper Half */}
                                            <div 
                                              className="flex-1 bg-blue-100 hover:bg-blue-200 cursor-pointer flex flex-col items-center justify-center border-b border-green-300"
                                              onClick={() => handleViewReservation(reservationInfo.reservation)}
                                            >
                                              <div className="text-[10px] font-bold text-blue-900 mb-0.5">RESERVED</div>
                                              <div className="text-[9px] text-blue-700">
                                                {reservationInfo.reservation.dropoff_time} - {reservationInfo.reservation.pickup_time}
                                              </div>
                                            </div>
                                            
                                            {/* Available Rest of Day - Lower Half */}
                                            <div className="flex-1 bg-green-100 flex flex-col items-center justify-center">
                                              <div className="text-[10px] font-bold text-green-900 mb-0.5">AVAILABLE</div>
                                              <div className="text-[9px] text-green-700">
                                                Rest of day
                                              </div>
                                            </div>
                                          </>
                                        ) : reservationInfo.isCheckInDay ? (
                                          // Check-in day: Available (12am to check-in) + Reserved (check-in to 12am)
                                          <>
                                            {/* Available Before Check-in - Upper Half */}
                                            <div className="flex-1 bg-green-100 flex flex-col items-center justify-center border-b border-blue-300">
                                              <div className="text-[10px] font-bold text-green-900 mb-0.5">AVAILABLE</div>
                                              <div className="text-[9px] text-green-700">
                                                12:00 AM - {reservationInfo.reservation.dropoff_time}
                                              </div>
                                            </div>
                                            
                                            {/* Reserved After Check-in - Lower Half */}
                                            <div 
                                              className="flex-1 bg-blue-100 hover:bg-blue-200 cursor-pointer flex flex-col items-center justify-center"
                                              onClick={() => handleViewReservation(reservationInfo.reservation)}
                                            >
                                              <div className="text-[10px] font-bold text-blue-900 mb-0.5">RESERVED</div>
                                              <div className="text-[9px] text-blue-700">
                                                {reservationInfo.reservation.dropoff_time} - 12:00 AM
                                              </div>
                                            </div>
                                          </>
                                        ) : (
                                          // Check-out day: Reserved (12am to check-out) + Available (check-out to 12am)
                                          <>
                                            {/* Reserved Until Check-out - Upper Half */}
                                            <div 
                                              className="flex-1 bg-blue-100 hover:bg-blue-200 cursor-pointer flex flex-col items-center justify-center border-b border-green-300"
                                              onClick={() => handleViewReservation(reservationInfo.reservation)}
                                            >
                                              <div className="text-[10px] font-bold text-blue-900 mb-0.5">RESERVED</div>
                                              <div className="text-[9px] text-blue-700">
                                                12:00 AM - {reservationInfo.reservation.pickup_time}
                                              </div>
                                            </div>
                                            
                                            {/* Available After Check-out - Lower Half */}
                                            <div className="flex-1 bg-green-100 flex flex-col items-center justify-center">
                                              <div className="text-[10px] font-bold text-green-900 mb-0.5">AVAILABLE</div>
                                              <div className="text-[9px] text-green-700">
                                                {reservationInfo.reservation.pickup_time} - 12:00 AM
                                              </div>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    ) : reservationInfo?.isMiddleDay ? (
                                      // Middle day of multi-day reservation: Fully reserved
                                      <div 
                                        className="rounded-lg border border-dotted border-blue-400 bg-blue-100 hover:bg-blue-200 cursor-pointer h-full flex flex-col items-center justify-center"
                                        onClick={() => handleViewReservation(reservationInfo.reservation)}
                                      >
                                        <div className="text-xs font-bold text-blue-900 mb-1">RESERVED</div>
                                        <div className="text-xs text-blue-700">Full Day</div>
                                        <div className="text-[9px] text-theme-blue mt-1">
                                          (Multi-day booking)
                                        </div>
                                      </div>
                                    ) : (
                                      // Single reservation or no reservation
                                      <div 
                                        className={`rounded-lg border border-dotted p-3 h-full flex flex-col ${
                                          hasReservation
                                            ? 'bg-blue-100 border-blue-400 cursor-pointer hover:bg-blue-200'
                                            : isBlocked 
                                              ? 'bg-red-50 border-red-300' 
                                              : isScheduled
                                                ? 'bg-green-100 border-green-400'
                                                : schedule.is_enabled 
                                                  ? 'bg-green-50 border-green-300' 
                                                  : 'bg-gray-50 border-gray-300'
                                        }`}
                                        onClick={() => {
                                          if (hasReservation) {
                                            handleViewReservation(reservationsOnDate[0]);
                                          }
                                        }}
                                      >
                                        <div className="flex-shrink-0 text-center mb-2">
                                          {hasReservation ? (
                                            <div>
                                              <div className="text-xs font-bold text-blue-900 mb-1">
                                                RESERVED
                                              </div>
                                              <div className="text-xs text-blue-700">
                                                {reservationsOnDate[0].dropoff_time} - {reservationsOnDate[0].pickup_time}
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="text-xs font-semibold text-gray-900">
                                              {schedule.slot_duration_minutes >= 1440 ? '24 Hours' : `${schedule.start_time} - ${schedule.end_time}`}
                                            </div>
                                          )}
                                        </div>
                                        {schedule.breaks && schedule.breaks.length > 0 && (
                                          <div className="flex-shrink-0 text-[10px] text-orange-600 font-medium mb-2 space-y-0.5">
                                            {schedule.breaks.map((brk, idx) => (
                                              <div key={idx}>
                                                🕐 {brk.start_time} - {brk.end_time}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        <div className="flex-grow"></div>
                                        <div className="table-y-2 mt-2 flex-shrink-0">
                                        <div className="flex items-center justify-between">
                                          <label className="flex items-center gap-1">
                                            <input
                                              type="checkbox"
                                              checked={!isBlocked}
                                              onChange={async () => {
                                                try {
                                                  await axios.post(
                                                    `${API_BASE_URL}/api/table-blocked-dates/bulk`,
                                                    {
                                                      table_id: table.id,
                                                      dates: [formatDateForAPI(date)],
                                                      is_blocked: !isBlocked,
                                                      reason: isBlocked ? 'Scheduled/Available' : 'Blocked by admin'
                                                    },
                                                    { headers: { Authorization: `Bearer ${token}` } }
                                                  );
                                                  fetchBlockedDates();
                                                  showMessage('success', 'Date availability updated');
                                                } catch (error) {
                                                  showMessage('error', 'Failed to update date');
                                                }
                                              }}
                                              className="rounded text-xs"
                                            />
                                            <span className="text-xs text-gray-600">{isBlocked ? 'Blocked' : 'On'}</span>
                                          </label>
                                          <button
                                            onClick={() => {
                                              const dateEntry = getDateStatus(table.id, date);
                                              const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                                              
                                              // Populate form with current schedule settings
                                              const is24Hours = schedule.slot_duration_minutes >= 1440 || (schedule.start_time === '00:00' && schedule.end_time === '23:59');
                                              const enableSlotBooking = schedule.slot_duration_minutes < 1440;
                                              
                                              setDateForm({
                                                is_24_hours: is24Hours,
                                                start_time: schedule.start_time || '09:00',
                                                end_time: schedule.end_time || '17:00',
                                                slot_duration_minutes: schedule.slot_duration_minutes || 30,
                                                enable_slot_booking: enableSlotBooking,
                                                breaks: schedule.breaks || []
                                              });
                                              
                                              setEditingDateEntry({ 
                                                ...dateEntry, 
                                                date: formatDateForAPI(date), 
                                                tableId: table.id,
                                                dayOfWeek: dayOfWeek 
                                              });
                                              setShowDateModal(true);
                                            }}
                                            className="text-theme-blue hover:text-blue-700"
                                          >
                                            <Edit3 className="h-3 w-3" />
                                          </button>
                                        </div>
                                      </div>
                                      </div>
                                    )
                                  ) : (isBlocked || isScheduled) ? (
                                    // No base schedule but has date-specific block/schedule
                                    <div className={`rounded-lg border border-dotted p-3 h-full flex flex-col ${
                                      isBlocked 
                                        ? 'bg-red-50 border-red-300' 
                                        : 'bg-green-100 border-green-400'
                                    }`}>
                                      {isScheduled && (() => {
                                        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                                        const schedule = getScheduleForDay(table.id, dayOfWeek);
                                        const is24Hours = schedule?.slot_duration_minutes >= 1440;
                                        
                                        return (
                                          <div className="flex-shrink-0 mb-2 text-center">
                                            <div className="text-xs font-semibold text-gray-900">
                                              {is24Hours ? '24 Hours' : `${schedule?.start_time || '09:00'} - ${schedule?.end_time || '17:00'}`}
                                            </div>
                                          </div>
                                        );
                                      })()}
                                      <div className="flex-grow"></div>
                                      <div className="flex items-center justify-between gap-2 flex-shrink-0">
                                        <label className="flex items-center gap-1">
                                          <input
                                            type="checkbox"
                                            checked={!isBlocked}
                                            onChange={async () => {
                                              try {
                                                await axios.post(
                                                  `${API_BASE_URL}/api/table-blocked-dates/bulk`,
                                                  {
                                                    table_id: table.id,
                                                    dates: [formatDateForAPI(date)],
                                                    is_blocked: !isBlocked,
                                                    reason: isBlocked ? 'Scheduled/Available' : 'Blocked by admin'
                                                  },
                                                  { headers: { Authorization: `Bearer ${token}` } }
                                                );
                                                fetchBlockedDates();
                                                showMessage('success', 'Date availability updated');
                                              } catch (error) {
                                                showMessage('error', 'Failed to update date');
                                              }
                                            }}
                                            className="rounded text-xs"
                                          />
                                          <span className="text-xs text-gray-600">{isBlocked ? 'Blocked' : 'On'}</span>
                                        </label>
                                        <button
                                          onClick={() => {
                                            const dateEntry = getDateStatus(table.id, date);
                                            const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                                            const schedule = getScheduleForDay(table.id, dayOfWeek);
                                            
                                            // Populate form with current schedule settings
                                            const is24Hours = schedule?.slot_duration_minutes >= 1440 || (schedule?.start_time === '00:00' && schedule?.end_time === '23:59');
                                            const enableSlotBooking = schedule?.slot_duration_minutes < 1440;
                                            
                                            setDateForm({
                                              is_24_hours: is24Hours,
                                              start_time: schedule?.start_time || '09:00',
                                              end_time: schedule?.end_time || '17:00',
                                              slot_duration_minutes: schedule?.slot_duration_minutes || 30,
                                              enable_slot_booking: enableSlotBooking
                                            });
                                            
                                            setEditingDateEntry({ 
                                              ...dateEntry, 
                                              date: formatDateForAPI(date), 
                                              tableId: table.id,
                                              dayOfWeek: dayOfWeek 
                                            });
                                            setShowDateModal(true);
                                          }}
                                          className="text-theme-blue hover:text-blue-700"
                                        >
                                          <Edit3 className="h-3 w-3" />
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="h-full">
                                      <button
                                        onClick={() => {
                                          const dateEntry = getDateStatus(table.id, date);
                                          const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                                          
                                          // Set default form values for new schedule
                                          setDateForm({
                                            is_24_hours: false,
                                            start_time: '09:00',
                                            end_time: '17:00',
                                            slot_duration_minutes: 30,
                                            enable_slot_booking: true
                                          });
                                          
                                          setEditingDateEntry({ 
                                            ...dateEntry, 
                                            date: formatDateForAPI(date), 
                                            tableId: table.id,
                                            dayOfWeek: dayOfWeek,
                                            is_blocked: false // Default to available when adding new schedule
                                          });
                                          setShowDateModal(true);
                                        }}
                                        className="w-full h-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg border border-dotted border-gray-300 transition-colors"
                                      >
                                        <Plus className="h-5 w-5" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Configure Form Tab */}
          {activeTab === 'configure-form' && (
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-4">Configure Booking Form</h3>
              
              {/* Section Tabs with Add Button */}
              <div className="flex items-center justify-between mb-6 border-b">
                <div className="flex table-x-2">
                  {sections.map(section => (
                    <button
                      key={section.value}
                      onClick={() => setSelectedSection(section.value)}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        selectedSection === section.value
                          ? 'border-[var(--theme-primary)] text-[var(--theme-primary)]'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {section.label}
                    </button>
                  ))}
                </div>
                
                {/* Add Field Button - Right side of tabs */}
                {isSectionEnabled() && (
                  <button
                    onClick={() => setShowAddFieldModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white rounded-md hover:opacity-90 transition-opacity mb-2"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Field
                  </button>
                )}
              </div>

              {/* Enable/Disable Toggle for Room Details and Special Instructions */}
              {selectedSection === 'special_instructions' && (
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Enable Special Instructions Section
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        When disabled, this section will not appear in the booking form
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={specialInstructionsEnabled}
                        onChange={(e) => {
                          setSpecialInstructionsEnabled(e.target.checked);
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                    </label>
                  </div>
                </div>
              )}

              {/* Fields List */}
              {isSectionEnabled() && (
                <>
                  <div className="table-y-2 mb-4">
                    {getCurrentFields().map((field, index) => (
                      <div key={field.id} className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-[var(--theme-primary)] transition-colors">
                        <div className="flex flex-col table-y-1 mr-3">
                          <button
                            onClick={() => handleMoveField(field.id, 'up')}
                            disabled={index === 0}
                            className="p-1 text-gray-400 hover:text-[var(--theme-primary)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <ArrowUp className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => handleMoveField(field.id, 'down')}
                            disabled={index === getCurrentFields().length - 1}
                            className="p-1 text-gray-400 hover:text-[var(--theme-primary)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <ArrowDown className="h-3 w-3" />
                          </button>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">{field.field_name}</span>
                            {field.required && <span className="text-xs text-red-600 font-semibold">*</span>}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{capitalizeFirstLetter(field.field_type)}</p>
                          {field.description && (
                            <p className="text-xs text-gray-500 mt-1">{field.description}</p>
                          )}
                        </div>
                        <div className="flex items-center table-x-2">
                          {!field.is_system && (
                            <>
                              <button
                                onClick={() => handleEditField(field)}
                                className="p-2 text-theme-blue hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <Edit3 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteField(field.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {field.is_system && (
                            <span className="text-xs text-gray-400 italic">Default</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Save Button */}
              <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={handleSaveAll}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Save className="h-4 w-4" />
                  {loading ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </div>
          )}

          {/* Configure Services Tab */}
          {activeTab === 'configure-services' && (
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-4">Configure Services</h3>
              
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Enable Service Selection</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Allow customers to select table services during reservation
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.services_enabled}
                      onChange={(e) => setSettings({ ...settings, services_enabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                  </label>
                </div>
              </div>

              {settings.services_enabled && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">Select Service Categories</p>
                  {serviceCategories.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">No service categories found</p>
                      <p className="text-xs text-gray-400 mt-1">Create service categories in Service Management</p>
                    </div>
                  ) : (
                    <div className="table-y-3">
                      {serviceCategories.map(category => (
                        <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="p-4 bg-gray-50 flex items-center justify-between">
                            <div className="flex items-center table-x-3">
                              <input
                                type="checkbox"
                                checked={selectedCategories.includes(category.id)}
                                onChange={() => handleCategoryToggle(category.id)}
                                className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                              />
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">{category.menu_category_name}</h4>
                                <p className="text-xs text-gray-500">
                                  {categoryServices[category.id]?.length || 0} service(s)
                                </p>
                              </div>
                            </div>
                          </div>
                          {selectedCategories.includes(category.id) && categoryServices[category.id] && (
                            <div className="p-4 bg-white border-t border-gray-200">
                              <p className="text-xs font-medium text-gray-700 mb-3">Services in this category:</p>
                              <div className="table-y-2">
                                {categoryServices[category.id].map(service => (
                                  <div key={service.id} className="p-3 bg-gray-50 rounded border border-gray-200 flex items-center justify-between gap-4">
                                    <div className="flex-1">
                                      <p className="font-medium text-gray-900 text-sm">{service.service_name}</p>
                                      <p className="text-gray-600 text-xs">{service.duration_minutes} min • ${service.price}</p>
                                    </div>
                                    
                                    {/* Table Category Selection */}
                                    <div className="flex-shrink-0 w-64">
                                      {tableCategories.length === 0 ? (
                                        <p className="text-xs text-gray-500 italic">No table categories</p>
                                      ) : (
                                        <div className="table-y-1">
                                          <p className="text-xs font-medium text-gray-700 mb-1">Assign to Table Categories:</p>
                                          <div className="flex flex-wrap gap-2">
                                            {tableCategories.map(tableCategory => {
                                              const currentMappings = serviceCategoryMappings[service.id] || [];
                                              const isSelected = Array.isArray(currentMappings) 
                                                ? currentMappings.includes(tableCategory.id)
                                                : currentMappings === tableCategory.id;
                                              
                                              return (
                                                <label
                                                  key={tableCategory.id}
                                                  className="inline-flex items-center"
                                                >
                                                  <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                      let newMappings = Array.isArray(currentMappings) 
                                                        ? [...currentMappings] 
                                                        : (currentMappings ? [currentMappings] : []);
                                                      
                                                      if (e.target.checked) {
                                                        if (!newMappings.includes(tableCategory.id)) {
                                                          newMappings.push(tableCategory.id);
                                                        }
                                                      } else {
                                                        newMappings = newMappings.filter(id => id !== tableCategory.id);
                                                      }
                                                      
                                                      setServiceCategoryMappings({
                                                        ...serviceCategoryMappings,
                                                        [service.id]: newMappings
                                                      });
                                                    }}
                                                    className="w-3 h-3 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                                                  />
                                                  <span className="ml-1 text-xs text-gray-700">{tableCategory.category_name}</span>
                                                </label>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Save Button */}
              <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={handleSaveAll}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Save className="h-4 w-4" />
                  {loading ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </div>
          )}

          {/* Upload Documents Tab */}
          {activeTab === 'upload-documents' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900">Document Upload Configuration</h3>
                
                {/* Add Upload Field Button - Top Right */}
                {settings.document_upload_enabled && (
                  <button
                    onClick={() => setShowAddDocumentModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white rounded-md hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Upload Field
                  </button>
                )}
              </div>
              
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Enable Document Upload</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Allow customers to upload documents during booking
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.document_upload_enabled}
                      onChange={(e) => setSettings({ ...settings, document_upload_enabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                  </label>
                </div>
              </div>

              {settings.document_upload_enabled && (
                <div>
                  <div className="table-y-3 mb-4">
                    {documentFields.map((doc) => (
                      <div key={doc.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900">{doc.title}</h4>
                            {doc.description && (
                              <p className="text-xs text-gray-600 mt-1">{doc.description}</p>
                            )}
                            <div className="mt-3 flex items-center table-x-2 text-xs text-gray-500">
                              <Upload className="h-3 w-3" />
                              <span>Upload field will appear in booking form</span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-4"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={handleSaveAll}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Save className="h-4 w-4" />
                  {loading ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Table Settings</h3>

              {/* Slot Bookings Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white mb-[5px]">
                <button
                  onClick={() => toggleAccordion('slotBookings')}
                  className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-gray-600" />
                    <span className="text-base font-semibold text-gray-900">Reservation Slot Settings</span>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-gray-600 transition-transform duration-300 ${openAccordions.slotBookings ? 'rotate-180' : ''}`} />
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${openAccordions.slotBookings ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="p-6 space-y-6 border-t border-gray-200">
              {/* Reservation Slots Configuration */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-500 mb-4">Configure time slots and capacity for table reservations</p>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Start Time</label>
                      <input
                        type="time"
                        value={settings.start_time || '09:00'}
                        onChange={(e) => setSettings({ ...settings, start_time: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">End Time</label>
                      <input
                        type="time"
                        value={settings.end_time || '22:00'}
                        onChange={(e) => setSettings({ ...settings, end_time: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Slot Interval (minutes)</label>
                      <select
                        value={settings.slot_duration || 30}
                        onChange={(e) => setSettings({ ...settings, slot_duration: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="15">15 minutes</option>
                        <option value="30">30 minutes</option>
                        <option value="60">1 hour</option>
                        <option value="90">1.5 hours</option>
                        <option value="120">2 hours</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Time intervals shown in booking form
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Reservation Duration (minutes)</label>
                      <select
                        value={settings.reservation_duration || 60}
                        onChange={(e) => setSettings({ ...settings, reservation_duration: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        {Array.from({ length: 16 }, (_, i) => (i + 1) * 15).map(minutes => (
                          <option key={minutes} value={minutes}>{minutes} min</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        How long each table is reserved
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Last Slot Buffer (minutes)</label>
                      <select
                        value={settings.last_slot_buffer || 30}
                        onChange={(e) => setSettings({ ...settings, last_slot_buffer: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="0">No buffer</option>
                        <option value="15">15 minutes</option>
                        <option value="30">30 minutes</option>
                        <option value="45">45 minutes</option>
                        <option value="60">1 hour</option>
                        <option value="90">1.5 hours</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Stop bookings before break/closing
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Maximum Guests per Booking</label>
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={settings.max_guests || 6}
                        onChange={(e) => setSettings({ ...settings, max_guests: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Max guests in dropdown
                      </p>
                    </div>
                  </div>

                  {/* Break Period Toggle */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Enable Break Period</p>
                        <p className="text-xs text-gray-500 mt-1">Block time slots during break hours (applies to all tables)</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.break_enabled || false}
                          onChange={(e) => setSettings({ ...settings, break_enabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-theme-blue"></div>
                      </label>
                    </div>

                    {settings.break_enabled && (
                      <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Break Start Time</label>
                          <input
                            type="time"
                            value={settings.break_start_time || '14:00'}
                            onChange={(e) => setSettings({ ...settings, break_start_time: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Break End Time</label>
                          <input
                            type="time"
                            value={settings.break_end_time || '15:00'}
                            onChange={(e) => setSettings({ ...settings, break_end_time: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Business Phone */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="mb-2">
                      <label className="block text-sm font-medium text-gray-900 mb-1">Business Phone Number</label>
                      <p className="text-xs text-gray-500 mb-2">Displayed when no tables are available</p>
                    </div>
                    <input
                      type="tel"
                      value={settings.business_phone || '+1 (555) 123-4567'}
                      onChange={(e) => setSettings({ ...settings, business_phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
              </div>
                  </div>
                </div>

              {/* Appointment Counter Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white mb-[5px]">
                <button
                  onClick={() => toggleAccordion('appointmentCounter')}
                  className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Hash className="h-5 w-5 text-gray-600" />
                    <span className="text-base font-semibold text-gray-900">Appointment Counter</span>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-gray-600 transition-transform duration-300 ${openAccordions.appointmentCounter ? 'rotate-180' : ''}`} />
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${openAccordions.appointmentCounter ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="p-6 border-t border-gray-200">
                    {/* Table Number Configuration */}
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm font-medium text-gray-900 mb-4">Table Number Configuration</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Prefix</label>
                          <input
                            type="text"
                            value={settings.table_number_prefix}
                            onChange={(e) => setSettings({ ...settings, table_number_prefix: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="TBL"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Starting Number</label>
                          <input
                            type="number"
                            value={settings.table_number_start}
                            onChange={(e) => setSettings({ ...settings, table_number_start: parseInt(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="1000"
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={handleResetCounter}
                          className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                          style={{ backgroundColor: primaryColor }}
                        >
                          Reset Counter
                        </button>
                        <button
                          onClick={() => setShowDeleteAllBookingsModal(true)}
                          className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                        >
                          Delete All Reservations
                        </button>
                      </div>

                      <p className="text-xs text-gray-500 mt-3">
                        Example: {settings.table_number_prefix}{settings.table_number_start}
                      </p>
                    </div>

                    {/* Tour Number Configuration */}
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mt-4">
                      <p className="text-sm font-medium text-gray-900 mb-4">Tour Appointment Number Configuration</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Prefix</label>
                          <input
                            type="text"
                            value={settings.tour_number_prefix || 'TOUR'}
                            onChange={(e) => setSettings({ ...settings, tour_number_prefix: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="TOUR"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Starting Number</label>
                          <input
                            type="number"
                            value={settings.tour_number_start || 1000}
                            onChange={(e) => setSettings({ ...settings, tour_number_start: parseInt(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="1000"
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to reset the tour appointment counter? This will start numbering from the beginning.')) {
                              try {
                                await axios.post(`${API_BASE_URL}/api/table/reset-tour-counter`, {}, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                showMessage('success', 'Tour counter reset successfully');
                              } catch (error) {
                                console.error('Error resetting tour counter:', error);
                                showMessage('error', 'Failed to reset tour counter');
                              }
                            }
                          }}
                          className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                          style={{ backgroundColor: primaryColor }}
                        >
                          Reset Tour Counter
                        </button>
                        <button
                          onClick={() => setShowDeleteAllToursModal(true)}
                          className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                        >
                          Delete All Tour Schedules
                        </button>
                      </div>

                      <p className="text-xs text-gray-500 mt-3">
                        Example: {settings.tour_number_prefix || 'TOUR'}{settings.tour_number_start || 1000}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Settings Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white mb-[5px]">
                <button
                  onClick={() => toggleAccordion('payments')}
                  className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <DollarSign className="h-5 w-5 text-gray-600" />
                    <span className="text-base font-semibold text-gray-900">Payment Settings</span>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-gray-600 transition-transform duration-300 ${openAccordions.payments ? 'rotate-180' : ''}`} />
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${openAccordions.payments ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="p-6 border-t border-gray-200">
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                
                <div className="mb-4">
                  {/* Table Payment Toggle */}
                  <div className="flex items-center justify-between mb-3 p-3 bg-white border border-gray-200 rounded-lg">
                    <div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-700">Enable Payment for Book Table</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Collect payments when customers book table appointments</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.table_payment_enabled || false}
                        onChange={(e) => setSettings({ ...settings, table_payment_enabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                    </label>
                  </div>

                  {(settings.tour_payment_enabled || settings.table_payment_enabled) && (
                    <div className="table-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Payment Gateway</label>
                        <select
                          value={settings.payment_mode}
                          onChange={(e) => setSettings({ ...settings, payment_mode: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="">Select Payment Gateway</option>
                          {availablePaymentModes.map(mode => (
                            <option key={mode.value} value={mode.value}>{mode.label}</option>
                          ))}
                        </select>
                        {availablePaymentModes.length === 0 && (
                          <p className="text-xs text-red-600 mt-1">No payment gateways configured. Configure in Payment Settings.</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Token/Deposit Amount ($)</label>
                        <input
                          type="number"
                          value={settings.token_amount}
                          onChange={(e) => setSettings({ ...settings, token_amount: parseFloat(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="0.00"
                          step="0.01"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

                    </div>
                  </div>
                </div>
              </div>

              {/* Notifications Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white mb-[5px]">
                <button
                  onClick={() => toggleAccordion('notifications')}
                  className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Bell className="h-5 w-5 text-gray-600" />
                    <span className="text-base font-semibold text-gray-900">Notifications</span>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-gray-600 transition-transform duration-300 ${openAccordions.notifications ? 'rotate-180' : ''}`} />
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${openAccordions.notifications ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="p-6 border-t border-gray-200">
                    <div className="table-y-4">
                      <div className="flex items-center justify-between py-3 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          <Mail className="h-5 w-5 text-gray-500" />
                          <div>
                            <span className="text-sm font-medium text-gray-900">Email Notifications</span>
                            <p className="text-xs text-gray-500">Send email updates for bookings</p>
                          </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.email_enabled}
                            onChange={(e) => setSettings({ ...settings, email_enabled: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between py-3">
                        <div className="flex items-center gap-3">
                          <MessageSquare className="h-5 w-5 text-gray-500" />
                          <div>
                            <span className="text-sm font-medium text-gray-900">SMS Notifications</span>
                            <p className="text-xs text-gray-500">Send SMS updates for bookings</p>
                          </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.sms_enabled}
                            onChange={(e) => setSettings({ ...settings, sms_enabled: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tax Settings Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white mb-[5px]">
                <button
                  onClick={() => toggleAccordion('taxSettings')}
                  className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Receipt className="h-5 w-5 text-gray-600" />
                    <span className="text-base font-semibold text-gray-900">Tax Settings</span>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-gray-600 transition-transform duration-300 ${openAccordions.taxSettings ? 'rotate-180' : ''}`} />
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${openAccordions.taxSettings ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="p-6 border-t border-gray-200">
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-sm text-gray-700">Enable Tax</span>
                      <p className="text-xs text-gray-500">Apply sales tax to table bookings</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.tax_enabled || false}
                        onChange={(e) => setSettings({ ...settings, tax_enabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                    </label>
                  </div>

                  {settings.tax_enabled && (
                    <div className="table-y-4 mt-4">
                      {/* Tax Type Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Tax Configuration</label>
                        <div className="table-y-2">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              checked={(settings.tax_type || 'state') === 'state'}
                              onChange={() => setSettings({ ...settings, tax_type: 'state' })}
                              className="h-4 w-4"
                              style={{ accentColor: primaryColor }}
                            />
                            <span className="ml-2 text-sm text-gray-700">Use State Tax Rate</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              checked={(settings.tax_type || 'state') === 'custom'}
                              onChange={() => setSettings({ ...settings, tax_type: 'custom' })}
                              className="h-4 w-4"
                              style={{ accentColor: primaryColor }}
                            />
                            <span className="ml-2 text-sm text-gray-700">Use Custom Tax Rate</span>
                          </label>
                        </div>
                      </div>

                      {/* State Selection or Custom Rate */}
                      {(settings.tax_type || 'state') === 'state' ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Select State</label>
                          <select
                            value={settings.tax_state || ''}
                            onChange={(e) => {
                              const stateCode = e.target.value;
                              const taxRates = {'CA': 7.25, 'NY': 4.0, 'TX': 6.25, 'FL': 6.0};
                              setSettings({ 
                                ...settings, 
                                tax_state: stateCode,
                                tax_rate: taxRates[stateCode] || 0
                              });
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            <option value="">Select a state</option>
                            <option value="CA">California (7.25%)</option>
                            <option value="NY">New York (4.0%)</option>
                            <option value="TX">Texas (6.25%)</option>
                            <option value="FL">Florida (6.0%)</option>
                          </select>
                        </div>
                      ) : (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Custom Tax Rate (%)</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            placeholder="0.00"
                            value={settings.tax_custom_rate || 0}
                            onChange={(e) => {
                              const rate = parseFloat(e.target.value) || 0;
                              setSettings({ ...settings, tax_custom_rate: rate, tax_rate: rate });
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      )}

                      {/* Current Tax Rate Display */}
                      {settings.tax_rate > 0 && (
                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Current Tax Rate</p>
                          <p className="text-xl font-bold text-gray-900">{settings.tax_rate}%</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleSaveAll}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Save className="h-4 w-4" />
                  {loading ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Add/Edit Table Modal */}
        {showTableModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col" style={{ maxHeight: '90vh' }}>
              {/* Sticky Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" style={{ backgroundColor: primaryColor }}>
                <h3 className="text-lg font-semibold text-white">
                  {editingTable ? 'Edit Table' : 'Add New Table'}
                </h3>
                <button
                  onClick={() => {
                    setShowTableModal(false);
                    setTableForm({ 
                      table_name: '', 
                      capacity: 2,
                      description: '',
                      category_id: '',
                      schedule_duration_months: 1,
                      is_24_hours: false,
                      start_time: '09:00',
                      end_time: '17:00',
                      slot_duration_minutes: 30,
                      enable_slot_booking: false,
                      bulk_start_date: '',
                      bulk_end_date: ''
                    });
                    setEditingTable(null);
                  }}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="px-6 py-5 space-y-6 overflow-y-auto flex-1">
                {/* Basic Information Section */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 pb-2 border-b border-gray-200">
                    Basic Information
                  </h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Table Name *
                    </label>
                    <input
                      type="text"
                      value={tableForm.table_name}
                      onChange={(e) => setTableForm({ ...tableForm, table_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., Corner Window Table, Patio Table 1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Seating Capacity *
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={tableForm.capacity}
                      onChange={(e) => setTableForm({ ...tableForm, capacity: parseInt(e.target.value) || 2 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., 2, 4, 6"
                    />
                    <p className="text-xs text-gray-500 mt-1.5">
                      Number of guests this table can accommodate (entire table is reserved per booking)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={tableForm.description}
                      onChange={(e) => setTableForm({ ...tableForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={3}
                      placeholder="Optional description about the table location or features"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      value={tableForm.category_id}
                      onChange={(e) => setTableForm({ ...tableForm, category_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">No Category</option>
                      {tableCategories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.category_name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1.5">
                      Assign a category to help organize and filter tables
                    </p>
                  </div>
                </div>

                {/* Schedule Configuration Section */}
                <div className="space-y-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-900 pb-2 border-b border-gray-200">
                    {editingTable ? 'Update Schedule Configuration' : 'Schedule Configuration'}
                  </h4>
                  
                  {!editingTable && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Schedule Duration *
                      </label>
                      <select
                        value={tableForm.schedule_duration_months}
                        onChange={(e) => setTableForm({ ...tableForm, schedule_duration_months: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="1">1 Month</option>
                        <option value="2">2 Months</option>
                        <option value="3">3 Months</option>
                        <option value="4">4 Months</option>
                        <option value="5">5 Months</option>
                        <option value="6">6 Months</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1.5">
                        Automatically create schedules for all days
                      </p>
                    </div>
                  )}

                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                    <h5 className="text-sm font-semibold text-blue-900">
                      {editingTable ? 'Bulk Update Availability' : 'Bulk Schedule/Block Dates (Optional)'}
                    </h5>
                    <p className="text-xs text-blue-700">
                      {editingTable 
                        ? 'Select a date range. Click "Schedule" to make dates available for booking, or "Block" to prevent bookings.'
                        : 'Select a date range. Click "Schedule" to make dates available for booking, or "Block" to keep them unavailable.'}
                    </p>
                    
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Start Date *</label>
                        <input
                          type="date"
                          value={tableForm.bulk_start_date || ''}
                          onChange={(e) => setTableForm({ ...tableForm, bulk_start_date: e.target.value })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">End Date *</label>
                        <input
                          type="date"
                          value={tableForm.bulk_end_date || ''}
                          onChange={(e) => setTableForm({ ...tableForm, bulk_end_date: e.target.value })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
                          min={tableForm.bulk_start_date || new Date().toISOString().split('T')[0]}
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleBulkCalendarUpdate('schedule')}
                        disabled={!tableForm.bulk_start_date || !tableForm.bulk_end_date}
                        className={`${editingTable ? 'flex-1' : 'w-full'} px-3 py-2 text-xs font-medium text-green-700 bg-green-100 border border-green-300 rounded hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                      >
                        ✓ Schedule Dates (Available)
                      </button>
                      <button
                        type="button"
                        onClick={() => handleBulkCalendarUpdate('block')}
                        disabled={!tableForm.bulk_start_date || !tableForm.bulk_end_date}
                        className={`${editingTable ? 'flex-1' : 'w-full'} px-3 py-2 text-xs font-medium text-red-700 bg-red-100 border border-red-300 rounded hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                      >
                        🚫 Block Dates (Unavailable)
                      </button>
                    </div>
                    
                    {editingTable && (
                      <p className="text-xs text-gray-600 mt-2">
                        Note: Individual day schedules can still be edited directly in the calendar below.
                      </p>
                    )}
                    
                    {!editingTable && tempBlockedDates.length > 0 && (
                      <p className="text-xs text-red-700 font-medium mt-2">
                        🚫 {tempBlockedDates.length} date(s) will be blocked (unavailable) when table is created
                      </p>
                    )}
                  </div>

                  <div className="space-y-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={tableForm.is_24_hours}
                        onChange={(e) => setTableForm({ ...tableForm, is_24_hours: e.target.checked })}
                        className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">Open 24 Hours</span>
                    </label>
                  </div>

                  {!tableForm.is_24_hours && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Start Time *
                        </label>
                        <input
                          type="time"
                          value={tableForm.start_time}
                          onChange={(e) => setTableForm({ ...tableForm, start_time: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          End Time *
                        </label>
                        <input
                          type="time"
                          value={tableForm.end_time}
                          onChange={(e) => setTableForm({ ...tableForm, end_time: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={tableForm.enable_slot_booking}
                        onChange={(e) => setTableForm({ ...tableForm, enable_slot_booking: e.target.checked })}
                        className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">Enable Hourly/Slot Booking</span>
                    </label>
                    <p className="text-xs text-gray-500 ml-6">
                      When disabled, users can book for multiple days (overnight table)
                    </p>
                  </div>

                  {tableForm.enable_slot_booking && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Slot Duration (minutes) *</label>
                      <input
                        type="number"
                        min="15"
                        step="15"
                        value={tableForm.slot_duration_minutes}
                        onChange={(e) => setTableForm({ ...tableForm, slot_duration_minutes: parseInt(e.target.value) || 30 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="30"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Duration of each booking slot (for hourly bookings)
                      </p>
                    </div>
                  )}

                  {/* Break Periods Section */}
                  {!tableForm.is_24_hours && (
                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <div className="flex items-center mb-3">
                        <input
                          type="checkbox"
                          checked={tableForm.breaks && tableForm.breaks.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTableForm({ ...tableForm, breaks: [{ start_time: '14:00', end_time: '17:00' }] });
                            } else {
                              setTableForm({ ...tableForm, breaks: [] });
                            }
                          }}
                          className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                        />
                        <label className="ml-2 text-sm font-medium text-gray-700">Enable Break Period</label>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">
                        Configure closed hours during the day (e.g., 2:00 PM - 5:00 PM)
                      </p>
                      
                      {tableForm.breaks && tableForm.breaks.length > 0 && (
                        <div className="space-y-3">
                          {tableForm.breaks.map((breakPeriod, index) => (
                            <div key={index} className="space-y-2">
                              <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-gray-700">
                                  Break Period {index + 1}
                                </label>
                                {tableForm.breaks.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newBreaks = tableForm.breaks.filter((_, i) => i !== index);
                                      setTableForm({ ...tableForm, breaks: newBreaks });
                                    }}
                                    className="text-xs text-red-600 hover:text-red-800"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Start Time</label>
                                  <input
                                    type="time"
                                    value={breakPeriod.start_time}
                                    onChange={(e) => {
                                      const newBreaks = [...tableForm.breaks];
                                      newBreaks[index].start_time = e.target.value;
                                      setTableForm({ ...tableForm, breaks: newBreaks });
                                    }}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">End Time</label>
                                  <input
                                    type="time"
                                    value={breakPeriod.end_time}
                                    onChange={(e) => {
                                      const newBreaks = [...tableForm.breaks];
                                      newBreaks[index].end_time = e.target.value;
                                      setTableForm({ ...tableForm, breaks: newBreaks });
                                    }}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          <button
                            type="button"
                            onClick={() => {
                              const newBreaks = [...(tableForm.breaks || []), { start_time: '14:00', end_time: '17:00' }];
                              setTableForm({ ...tableForm, breaks: newBreaks });
                            }}
                            className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors border border-blue-200"
                          >
                            + Add Another Break
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {!tableForm.enable_slot_booking && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800">
                        ✓ Multi-day booking enabled - Users can book this table for overnight stays and extended periods
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Sticky Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-white">
                <button
                  onClick={() => {
                    setShowTableModal(false);
                    setTableForm({ 
                      table_name: '', 
                      capacity: 2,
                      description: '',
                      category_id: '',
                      schedule_duration_months: 1,
                      is_24_hours: false,
                      start_time: '09:00',
                      end_time: '17:00',
                      slot_duration_minutes: 30,
                      enable_slot_booking: false,
                      bulk_start_date: '',
                      bulk_end_date: ''
                    });
                    setEditingTable(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddTable}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                  style={{ backgroundColor: primaryColor }}
                >
                  {loading ? 'Saving...' : editingTable ? 'Update Table' : 'Add Table'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Schedule Modal */}
        {showScheduleModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
              <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: primaryColor }}>
                <h3 className="text-lg font-semibold text-white">
                  {editingSchedule?.id ? 'Edit Schedule' : 'Add Schedule'} - {editingSchedule?.day_of_week}
                </h3>
              </div>

              <div className="px-6 py-4 table-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Time *</label>
                    <input
                      type="time"
                      value={scheduleForm.start_time}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, start_time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Time *</label>
                    <input
                      type="time"
                      value={scheduleForm.end_time}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, end_time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Slot Duration (minutes) *</label>
                  <input
                    type="number"
                    min="15"
                    step="15"
                    value={scheduleForm.slot_duration_minutes}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, slot_duration_minutes: parseInt(e.target.value) || 30 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="30"
                  />
                </div>

                {/* Break Periods Section */}
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      checked={scheduleForm.breaks && scheduleForm.breaks.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setScheduleForm({ ...scheduleForm, breaks: [{ start_time: '14:00', end_time: '17:00' }] });
                        } else {
                          setScheduleForm({ ...scheduleForm, breaks: [] });
                        }
                      }}
                      className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                    />
                    <label className="ml-2 text-sm font-medium text-gray-700">Enable Break Period</label>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Configure closed hours during the day (e.g., 2:00 PM - 5:00 PM)
                  </p>
                  
                  {scheduleForm.breaks && scheduleForm.breaks.length > 0 && (
                    <div className="space-y-3">
                      {scheduleForm.breaks.map((breakPeriod, index) => (
                        <div key={index} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700">
                              Break Period {index + 1}
                            </label>
                            {scheduleForm.breaks.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newBreaks = scheduleForm.breaks.filter((_, i) => i !== index);
                                  setScheduleForm({ ...scheduleForm, breaks: newBreaks });
                                }}
                                className="text-xs text-red-600 hover:text-red-800"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Start Time</label>
                              <input
                                type="time"
                                value={breakPeriod.start_time}
                                onChange={(e) => {
                                  const newBreaks = [...scheduleForm.breaks];
                                  newBreaks[index].start_time = e.target.value;
                                  setScheduleForm({ ...scheduleForm, breaks: newBreaks });
                                }}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">End Time</label>
                              <input
                                type="time"
                                value={breakPeriod.end_time}
                                onChange={(e) => {
                                  const newBreaks = [...scheduleForm.breaks];
                                  newBreaks[index].end_time = e.target.value;
                                  setScheduleForm({ ...scheduleForm, breaks: newBreaks });
                                }}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      <button
                        type="button"
                        onClick={() => {
                          const newBreaks = [...(scheduleForm.breaks || []), { start_time: '14:00', end_time: '17:00' }];
                          setScheduleForm({ ...scheduleForm, breaks: newBreaks });
                        }}
                        className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors border border-blue-200"
                      >
                        + Add Another Break
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={scheduleForm.is_enabled}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, is_enabled: e.target.checked })}
                    className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                  />
                  <label className="ml-2 text-sm text-gray-700">Enable this schedule</label>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowScheduleModal(false);
                    setScheduleForm({ start_time: '09:00', end_time: '17:00', slot_duration_minutes: 30, is_enabled: true, breaks: [] });
                    setEditingSchedule(null);
                    setSelectedTableId(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSchedule}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                  style={{ backgroundColor: primaryColor }}
                >
                  {loading ? 'Saving...' : editingSchedule?.id ? 'Update Schedule' : 'Add Schedule'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Field Modal */}
        {showAddFieldModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col" style={{ maxHeight: '90vh' }}>
              {/* Sticky Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" style={{ backgroundColor: primaryColor }}>
                <h3 className="text-lg font-semibold text-white">{editingField ? 'Edit Field' : 'Add Form Field'}</h3>
                <button onClick={resetFieldForm}>
                  <X className="h-5 w-5 text-white" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto px-6 py-6">
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Field Name *</label>
                    <input
                      type="text"
                      value={fieldForm.field_name}
                      onChange={(e) => setFieldForm({ ...fieldForm, field_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Enter field name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Field Type *</label>
                    <select
                      value={fieldForm.field_type}
                      onChange={(e) => setFieldForm({ ...fieldForm, field_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      {fieldTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  {(fieldForm.field_type === 'dropdown' || fieldForm.field_type === 'radio') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Options (comma-separated)</label>
                      <input
                        type="text"
                        value={fieldForm.options}
                        onChange={(e) => setFieldForm({ ...fieldForm, options: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Option 1, Option 2, Option 3"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Placeholder</label>
                    <input
                      type="text"
                      value={fieldForm.placeholder}
                      onChange={(e) => setFieldForm({ ...fieldForm, placeholder: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Enter placeholder text"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={fieldForm.description}
                      onChange={(e) => setFieldForm({ ...fieldForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      rows={3}
                      placeholder="Optional description or help text"
                    />
                  </div>

                  <div className="flex items-center pt-1">
                    <input
                      type="checkbox"
                      checked={fieldForm.required}
                      onChange={(e) => setFieldForm({ ...fieldForm, required: e.target.checked })}
                      className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                    />
                    <label className="ml-2 text-sm text-gray-700">Required field</label>
                  </div>
                </div>
              </div>

              {/* Sticky Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={resetFieldForm}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddField}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                  style={{ backgroundColor: primaryColor }}
                >
                  {editingField ? 'Update Field' : 'Add Field'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Document Modal */}
        {showAddDocumentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" style={{ backgroundColor: primaryColor }}>
                <h3 className="text-lg font-semibold text-white">Add Upload Field</h3>
                <button onClick={() => setShowAddDocumentModal(false)}>
                  <X className="h-5 w-5 text-white" />
                </button>
              </div>

              <div className="px-6 py-4 table-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    value={documentForm.title}
                    onChange={(e) => setDocumentForm({ ...documentForm, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., Vaccination Records"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={documentForm.description}
                    onChange={(e) => setDocumentForm({ ...documentForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows={3}
                    placeholder="Optional description or instructions"
                  />
                </div>

                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg text-xs text-blue-800">
                  <Upload className="h-4 w-4" />
                  <span>Customers will see an upload button for this document in the booking form</span>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowAddDocumentModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddDocument}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg flex items-center gap-2"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Check className="h-4 w-4" />
                  Add Field
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Date Modal */}
      {showDateModal && editingDateEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" style={{ backgroundColor: primaryColor }}>
              <h3 className="text-lg font-semibold text-white">
                Edit Date Status
              </h3>
              <button
                onClick={() => {
                  setShowDateModal(false);
                  setEditingDateEntry(null);
                }}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="text"
                  value={new Date(editingDateEntry.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed text-sm"
                />
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!editingDateEntry.is_blocked}
                    onChange={(e) => {
                      setEditingDateEntry({ ...editingDateEntry, is_blocked: !e.target.checked });
                    }}
                    className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                  />
                  <span className="text-sm font-medium text-gray-700">On <span className="text-gray-500">(Block)</span></span>
                </label>
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={dateForm.is_24_hours}
                    onChange={(e) => setDateForm({ ...dateForm, is_24_hours: e.target.checked })}
                    className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                  />
                  <span className="text-sm font-medium text-gray-700">24 Hours Available</span>
                </label>
              </div>

              {!dateForm.is_24_hours && (
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                    <input
                      type="time"
                      value={dateForm.start_time}
                      onChange={(e) => setDateForm({ ...dateForm, start_time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                    <input
                      type="time"
                      value={dateForm.end_time}
                      onChange={(e) => setDateForm({ ...dateForm, end_time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              )}
              
              <div className="mb-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={dateForm.enable_slot_booking}
                    onChange={(e) => setDateForm({ ...dateForm, enable_slot_booking: e.target.checked })}
                    className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                  />
                  <span className="text-sm font-medium text-gray-700">Enable Slot Booking</span>
                </label>
              </div>
              
              {dateForm.enable_slot_booking && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slot Duration (minutes)</label>
                  <input
                    type="number"
                    value={dateForm.slot_duration_minutes}
                    onChange={(e) => setDateForm({ ...dateForm, slot_duration_minutes: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    min="15"
                    step="15"
                  />
                </div>
              )}

              {/* Break Periods Section */}
              {!dateForm.is_24_hours && (
                <div className="mb-4 border-t border-gray-200 pt-4">
                  <div className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      checked={dateForm.breaks && dateForm.breaks.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setDateForm({ ...dateForm, breaks: [{ start_time: '14:00', end_time: '17:00' }] });
                        } else {
                          setDateForm({ ...dateForm, breaks: [] });
                        }
                      }}
                      className="w-4 h-4 text-[var(--theme-primary)] border-gray-300 rounded focus:ring-[var(--theme-primary)]"
                    />
                    <label className="ml-2 text-sm font-medium text-gray-700">Enable Break Period</label>
                  </div>
                  
                  {dateForm.breaks && dateForm.breaks.length > 0 && (
                    <div className="space-y-3 mt-3">
                      {dateForm.breaks.map((breakPeriod, index) => (
                        <div key={index} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-medium text-gray-700">
                              Break Period {index + 1}
                            </label>
                            {dateForm.breaks.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newBreaks = dateForm.breaks.filter((_, i) => i !== index);
                                  setDateForm({ ...dateForm, breaks: newBreaks });
                                }}
                                className="text-xs text-red-600 hover:text-red-800"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Start</label>
                              <input
                                type="time"
                                value={breakPeriod.start_time}
                                onChange={(e) => {
                                  const newBreaks = [...dateForm.breaks];
                                  newBreaks[index].start_time = e.target.value;
                                  setDateForm({ ...dateForm, breaks: newBreaks });
                                }}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">End</label>
                              <input
                                type="time"
                                value={breakPeriod.end_time}
                                onChange={(e) => {
                                  const newBreaks = [...dateForm.breaks];
                                  newBreaks[index].end_time = e.target.value;
                                  setDateForm({ ...dateForm, breaks: newBreaks });
                                }}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      <button
                        type="button"
                        onClick={() => {
                          const newBreaks = [...(dateForm.breaks || []), { start_time: '14:00', end_time: '17:00' }];
                          setDateForm({ ...dateForm, breaks: newBreaks });
                        }}
                        className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 border border-blue-200"
                      >
                        + Add Another Break
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-6">
                <button
                  onClick={async () => {
                    try {
                      // Update schedule settings for this day of week
                      const schedule = getScheduleForDay(editingDateEntry.spaceId, editingDateEntry.dayOfWeek);
                      
                      const scheduleData = {
                        start_time: dateForm.is_24_hours ? '00:00' : dateForm.start_time,
                        end_time: dateForm.is_24_hours ? '23:59' : dateForm.end_time,
                        slot_duration_minutes: dateForm.is_24_hours ? 1440 : (dateForm.enable_slot_booking ? dateForm.slot_duration_minutes : 1440),
                        is_enabled: true,
                        breaks: dateForm.breaks || []
                      };
                      
                      if (schedule) {
                        // Update existing schedule
                        await axios.put(
                          `${API_BASE_URL}/api/table-schedules/${schedule.id}`,
                          scheduleData,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                      } else {
                        // Create new schedule for this day of week
                        await axios.post(
                          `${API_BASE_URL}/api/table-schedules`,
                          {
                            ...scheduleData,
                            table_id: editingDateEntry.spaceId,
                            day_of_week: editingDateEntry.dayOfWeek,
                            breaks: []
                          },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                      }
                      
                      // Update blocked/available status for this specific date
                      await axios.post(
                        `${API_BASE_URL}/api/table-blocked-dates/bulk`,
                        {
                          table_id: editingDateEntry.spaceId,
                          dates: [editingDateEntry.date],
                          is_blocked: editingDateEntry.is_blocked,
                          reason: editingDateEntry.is_blocked ? 'Blocked by admin' : 'Scheduled/Available for booking'
                        },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      
                      // Small delay to ensure database is updated
                      await new Promise(resolve => setTimeout(resolve, 300));
                      
                      // Refresh data before closing modal
                      await fetchTableSchedules();
                      await fetchBlockedDates();
                      
                      // Close modal and show success message
                      setShowDateModal(false);
                      setEditingDateEntry(null);
                      showMessage('success', 'Changes saved successfully');
                    } catch (error) {
                      console.error('Save error:', error);
                      showMessage('error', 'Failed to save changes');
                    }
                  }}
                  className="w-full px-4 py-2 text-sm font-medium bg-theme-blue text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Manage Categories Modal */}
        {showCategoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" style={{ backgroundColor: primaryColor }}>
                <h3 className="text-lg font-semibold text-white">Manage Table Categories</h3>
                <button
                  onClick={() => {
                    setShowCategoryModal(false);
                    setEditingCategory(null);
                    setCategoryForm({ category_name: '', description: '' });
                  }}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Add/Edit Category Form */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    {editingCategory ? 'Edit Category' : 'Add New Category'}
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category Name *</label>
                      <input
                        type="text"
                        value={categoryForm.category_name}
                        onChange={(e) => setCategoryForm({ ...categoryForm, category_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        placeholder="e.g., Indoor, Outdoor, VIP Suite"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={categoryForm.description}
                        onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        rows={3}
                        maxLength={250}
                        placeholder="Brief description (optional, ~50 words)"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {categoryForm.description.length}/250 characters
                      </p>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={handleSaveCategory}
                        disabled={loading}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {editingCategory ? 'Update Category' : 'Add Category'}
                      </button>
                      {editingCategory && (
                        <button
                          onClick={() => {
                            setEditingCategory(null);
                            setCategoryForm({ category_name: '', description: '' });
                          }}
                          className="px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Categories List */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Existing Categories</h4>
                  {tableCategories.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      No categories yet. Add your first category above.
                    </div>
                  ) : (
                    <div className="table-y-2">
                      {tableCategories.map(category => (
                        <div key={category.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-900">{category.category_name}</div>
                            {category.description && (
                              <div className="text-xs text-gray-500 mt-0.5">{category.description}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setEditingCategory(category);
                                setCategoryForm({
                                  category_name: category.category_name,
                                  description: category.description || ''
                                });
                              }}
                              className="p-1.5 text-theme-blue hover:bg-blue-50 rounded"
                              title="Edit category"
                            >
                              <Edit3 className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => {
                                setCategoryToDelete(category);
                                setShowDeleteCategoryModal(true);
                              }}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title="Delete category"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => {
                    setShowCategoryModal(false);
                    setEditingCategory(null);
                    setCategoryForm({ category_name: '', description: '' });
                  }}
                  className="px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Field Confirmation Modal */}
        {showDeleteFieldModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Delete Field</h3>
                    <p className="text-sm text-gray-500 mt-0.5">This action cannot be undone</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-4">
                <p className="text-sm text-gray-700">
                  Are you sure you want to delete the field <span className="font-semibold">&ldquo;{fieldToDelete?.fieldName}&rdquo;</span>?
                </p>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteFieldModal(false);
                    setFieldToDelete(null);
                  }}
                  className="px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteField}
                  className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete Field
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reservation Details Modal */}
        {showReservationModal && selectedReservation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: primaryColor }}>
                <div>
                  <h3 className="text-base font-semibold text-white">
                    {isEditMode ? 'Edit Reservation' : 'Reservation Details'}
                  </h3>
                  <p className="text-xs text-white text-opacity-90 mt-0.5">
                    #{selectedReservation.reservation_number || selectedReservation.id?.slice(0, 8)}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowReservationModal(false);
                    setSelectedReservation(null);
                    setIsEditMode(false);
                  }}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 table-y-6 overflow-y-auto flex-1 scrollbar-thin">
                {!modalDataReady ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-blue mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading reservation data...</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Reservation Time & Party Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200 shadow-sm">
                        <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          Date & Time
                        </div>
                        {isEditMode ? (
                          <div className="space-y-2">
                            <input
                              type="date"
                              value={editForm.dropoff_date || ''}
                              onChange={(e) => setEditForm({...editForm, dropoff_date: e.target.value})}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                            <input
                              type="time"
                              value={editForm.dropoff_time || ''}
                              onChange={(e) => setEditForm({...editForm, dropoff_time: e.target.value})}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                          </div>
                        ) : (
                          <>
                            <div className="text-sm font-semibold text-gray-900">
                              {selectedReservation.dropoff_date ? new Date(selectedReservation.dropoff_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
                            </div>
                            <div className="text-sm text-gray-700 mt-1 flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              {selectedReservation.dropoff_time ? new Date(`2000-01-01T${selectedReservation.dropoff_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'N/A'}
                            </div>
                          </>
                        )}
                      </div>

                      <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200 shadow-sm">
                        <div className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                          <Users className="h-3.5 w-3.5" />
                          Party Details
                        </div>
                        <div className="text-sm font-semibold text-gray-900">
                          {selectedReservation.guest_count || 'N/A'} {selectedReservation.guest_count === 1 ? 'Guest' : 'Guests'}
                        </div>
                        {selectedReservation.duration_minutes && (
                          <div className="text-sm text-gray-700 mt-1">
                            Duration: {selectedReservation.duration_minutes} mins
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Assigned Table & Category */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Assigned Table</h4>
                      {isEditMode ? (
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs font-medium text-gray-500 mb-1 block">Category</label>
                            <select
                              value={editForm.category_id || ''}
                              onChange={(e) => {
                                setEditForm({...editForm, category_id: e.target.value, assigned_table_id: ''});
                              }}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            >
                              <option value="">Select Category</option>
                              <option value="all">All Categories</option>
                              {tableCategories.map(cat => (
                                <option key={cat.id} value={cat.id}>
                                  {cat.category_name}
                                </option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                              Select a specific category to filter tables, or choose "All Categories" to see all available tables
                            </p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500 mb-1 block">Table</label>
                            <select
                              value={editForm.assigned_table_id || ''}
                              onChange={(e) => setEditForm({...editForm, assigned_table_id: e.target.value})}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                              disabled={!editForm.category_id}
                            >
                              <option value="">Select Table</option>
                              {(() => {
                                // Filter tables based on selected category
                                let tablesToShow = [];
                                if (editForm.category_id === 'all') {
                                  // Show all tables with category names
                                  tablesToShow = tables;
                                } else if (editForm.category_id) {
                                  // Show only tables from selected category
                                  tablesToShow = tables.filter(t => t.category_id === editForm.category_id);
                                }
                                
                                return tablesToShow.map(table => {
                                  const category = tableCategories.find(c => c.id === table.category_id);
                                  const displayName = editForm.category_id === 'all' && category
                                    ? `${table.table_name} (${category.category_name})`
                                    : table.table_name;
                                  
                                  return (
                                    <option key={table.id} value={table.id}>
                                      {displayName}
                                    </option>
                                  );
                                });
                              })()}
                            </select>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500">Category:</span>
                            <span className="text-sm font-medium text-gray-900">
                              {(() => {
                                // Find the category name for the assigned table
                                const assignedTable = tables.find(t => t.id === selectedReservation.assigned_table_id);
                                const category = tableCategories.find(c => c.id === assignedTable?.category_id);
                                return category?.category_name || 'N/A';
                              })()}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500">Table:</span>
                            <span className="text-sm font-medium text-gray-900">
                              {selectedReservation.assigned_table_name || 'N/A'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Customer & Form Details */}
                    {selectedReservation.form_details && Object.keys(selectedReservation.form_details).length > 0 && (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Customer Information</h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(selectedReservation.form_details)
                            .map(([key, value]) => {
                              const fieldName = getFieldNameById(key, 'form');
                              // Convert snake_case to Title Case, or capitalize single words
                              const displayName = fieldName.includes('_') 
                                ? fieldName.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')
                                : fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
                              return { key, value, fieldName, displayName };
                            })
                            .filter(({ fieldName, key }) => {
                              // Filter out fields with excessive underscores or special patterns
                              const fieldNameLower = fieldName.toLowerCase();
                              const hasExcessiveUnderscores = (fieldName.match(/_/g) || []).length > 2;
                              const hasSpecialPattern = fieldNameLower.includes('_for_') || fieldNameLower.includes('_-_') || fieldNameLower.includes('default_');
                              // Check if it looks like a UUID
                              const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(fieldName);
                              // Skip guest_count as it's shown in Party Details
                              const isGuestCount = key === 'guest_count' || key === 'number_of_guests' || fieldNameLower === 'guest_count' || fieldNameLower === 'number_of_guests';
                              
                              return !hasExcessiveUnderscores && !hasSpecialPattern && !isUuid && !isGuestCount;
                            })
                            .map(({ key, value, displayName }) => (
                              <div key={key}>
                                <label className="text-xs font-medium text-gray-500">{displayName}</label>
                                <p className="text-sm text-gray-900 mt-0.5">{value || 'N/A'}</p>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Special Instructions / Requests */}
                    {selectedReservation.special_instructions && Object.keys(selectedReservation.special_instructions).length > 0 && (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Special Requests</h4>
                        <div className="space-y-2">
                          {Object.entries(selectedReservation.special_instructions)
                            .map(([key, value]) => {
                              const fieldName = getFieldNameById(key, 'special');
                              // Convert snake_case to Title Case, or capitalize single words
                              const displayName = fieldName.includes('_') 
                                ? fieldName.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')
                                : fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
                              return { key, value, fieldName, displayName };
                            })
                            .filter(({ fieldName }) => {
                              // Filter out ugly fields
                              const fieldNameLower = fieldName.toLowerCase();
                              const hasExcessiveUnderscores = (fieldName.match(/_/g) || []).length > 2;
                              const hasSpecialPattern = fieldNameLower.includes('_for_') || fieldNameLower.includes('_-_') || fieldNameLower.includes('default_');
                              const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(fieldName);
                              
                              return !hasExcessiveUnderscores && !hasSpecialPattern && !isUuid;
                            })
                            .map(({ key, value, displayName }) => (
                              <div key={key}>
                                <label className="text-xs font-medium text-gray-500">{displayName}</label>
                                <p className="text-sm text-gray-900 mt-0.5 whitespace-pre-wrap">{value || 'N/A'}</p>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-2 border-t border-gray-200 flex items-center justify-between flex-shrink-0 bg-white">
                {isEditMode ? (
                  <>
                    {/* Status Dropdown - Left Side */}
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-medium text-gray-600">Status:</label>
                      <select
                        value={editForm.status || ''}
                        onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="confirmed">Confirmed</option>
                        <option value="checked_in">Checked In</option>
                        <option value="checked_out">Checked Out</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    
                    {/* Buttons - Right Side */}
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveReservationEdit}
                        className="px-4 py-1.5 text-sm text-white rounded-md font-medium transition-colors flex items-center gap-1.5"
                        style={{ backgroundColor: primaryColor }}
                      >
                        <Save className="h-3.5 w-3.5" />
                        Update Reservation
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Status Display - Left Side */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-600">Status:</span>
                      <span className="px-3 py-1 text-sm font-semibold text-gray-900 capitalize bg-gray-100 rounded-lg">
                        {selectedReservation.status}
                      </span>
                    </div>
                    
                    {/* Buttons - Right Side */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setIsEditMode(true)}
                        className="px-3 py-1.5 text-sm text-white rounded-md font-medium transition-colors flex items-center gap-1.5"
                        style={{ backgroundColor: primaryColor }}
                      >
                        <Edit3 className="h-3.5 w-3.5" />
                        Edit
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

      {/* Compact Loading Modal */}
      {isUpdating && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg px-6 py-4 flex items-center gap-3 shadow-xl">
            {/* Animated Circular Spinner */}
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-theme-blue border-t-transparent"></div>
            {/* Loading Text */}
            <p className="text-sm font-medium text-gray-700">Updating reservation...</p>
          </div>
        </div>
      )}

      {/* Custom Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-4 flex items-center gap-3">
              <div className="bg-white bg-opacity-20 rounded-full p-2">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">No Table Available</h3>
            </div>
            
            {/* Content */}
            <div className="px-6 py-5">
              <p className="text-gray-700 text-sm leading-relaxed">
                {errorMessage}
              </p>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 flex justify-end">
              <button
                onClick={handleErrorModalClose}
                className="px-5 py-2 text-sm font-semibold text-white rounded-lg transition-colors shadow-sm"
                style={{ backgroundColor: primaryColor }}
              >
                OK, Got It
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Service Options Modal */}
      {showOptionsModal && editForm.selected_service && (() => {
        // Get the service with full options - check modalServices if editForm doesn't have them
        const serviceWithOptions = editForm.selected_service.options && editForm.selected_service.options.length > 0
          ? editForm.selected_service
          : modalServices.find(s => s.id === editForm.selected_service_id) || editForm.selected_service;
        
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: `${primaryColor}10` }}>
              <h3 className="text-lg font-bold text-gray-900">
                Service Options: {serviceWithOptions.service_name || serviceWithOptions.name}
              </h3>
              <button
                onClick={() => setShowOptionsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Content - Scrollable */}
            <div className="px-6 py-4 overflow-y-auto flex-1 scrollbar-thin">
              <p className="text-sm text-gray-600 mb-4">
                Review and select options for your service. Previously selected options have been carried over where available.
              </p>
              
              <div className="table-y-4">
                {serviceWithOptions.options?.map((option) => (
                  <div key={option.option_id} className="border border-gray-200 rounded-lg p-4">
                    <label className="text-sm font-semibold text-gray-900 mb-2 block">
                      {option.option_name}
                      {option.is_required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    
                    {option.option_type === 'single' || option.option_type === 'checkbox' ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedServiceOptions[option.option_name] === true}
                          onChange={(e) => handleOptionChange(option.option_name, e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300"
                          style={{ accentColor: primaryColor }}
                        />
                        <span className="text-sm text-gray-700">
                          {option.price > 0 && `+$${option.price}`}
                        </span>
                      </div>
                    ) : option.option_type === 'dropdown' ? (
                      <select
                        value={selectedServiceOptions[option.option_name]?.choice_id || ''}
                        onChange={(e) => {
                          const choice = option.choices.find(c => c.choice_id === e.target.value);
                          handleOptionChange(option.option_name, choice ? { choice_id: choice.choice_id, choice_name: choice.choice_name } : null);
                        }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                      >
                        <option value="">Select...</option>
                        {option.choices?.map((choice) => (
                          <option key={choice.choice_id} value={choice.choice_id}>
                            {choice.choice_name} {choice.price > 0 && `(+$${choice.price})`}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-2 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => setShowOptionsModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Skip Options
              </button>
              <button
                onClick={handleOptionsConfirm}
                className="px-4 py-2 text-sm font-semibold text-white rounded-lg"
                style={{ backgroundColor: primaryColor }}
              >
                Confirm Options
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Delete All Reservations Confirmation Modal */}
      {showDeleteAllBookingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Delete All Reservations?</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete <strong>ALL table bookings</strong>? This will free up all calendar tables but the action <strong>cannot be undone</strong>.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteAllBookingsModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAllBookings}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete All Reservations
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Tour Schedules Confirmation Modal */}
      {showDeleteAllToursModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Delete All Tour Schedules?</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete <strong>ALL tour schedules</strong>? This action <strong>cannot be undone</strong>.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteAllToursModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAllTours}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete All Tour Schedules
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Category Confirmation Modal */}
      {showDeleteCategoryModal && categoryToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Delete Category</h3>
            </div>
            
            <div className="px-6 py-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700">
                    Are you sure you want to delete the category{' '}
                    <span className="font-semibold">"{categoryToDelete.category_name}"</span>?
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteCategoryModal(false);
                  setCategoryToDelete(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCategory}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Table Confirmation Modal */}
      {showDeleteTableModal && tableToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Delete Table</h3>
            </div>
            
            <div className="px-6 py-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700">
                    Are you sure you want to delete the table{' '}
                    <span className="font-semibold">"{tableToDelete.table_name}"</span>?
                  </p>
                  <p className="text-sm text-red-600 mt-2 font-medium">
                    All schedules for this table will also be deleted.
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteTableModal(false);
                  setTableToDelete(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTable}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {loading ? 'Deleting...' : 'Delete Table'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block/Unblock Slot Modal */}
      {showBlockSlotModal && slotToBlock && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            {/* Header */}
            <div className={`px-6 py-4 border-b border-gray-200 ${slotToBlock.isUnblocking ? 'bg-green-50' : 'bg-yellow-50'}`}>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <AlertCircle className={`h-5 w-5 ${slotToBlock.isUnblocking ? 'text-green-600' : 'text-yellow-600'}`} />
                {slotToBlock.isUnblocking ? 'Unblock Time Slot' : 'Block Time Slot'}
              </h3>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Warning for partial block */}
              {!slotToBlock.isUnblocking && slotToBlock.isPartialBlock && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-yellow-800 mb-1">Limited Availability</h4>
                      <p className="text-sm text-yellow-700">
                        Only <strong>{slotToBlock.availableSlots} consecutive slots</strong> ({slotToBlock.duration} minutes) are available. 
                        The full {slotToBlock.requestedDuration} minutes cannot be blocked due to existing bookings.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Table:</span>
                    <span className="font-semibold text-gray-900">{slotToBlock.table.table_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date:</span>
                    <span className="font-semibold text-gray-900">
                      {slotToBlock.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Start Time:</span>
                    <span className="font-semibold text-gray-900">{slotToBlock.timeSlot}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Duration:</span>
                    <span className={`font-semibold ${slotToBlock.isPartialBlock && !slotToBlock.isUnblocking ? 'text-yellow-700' : 'text-gray-900'}`}>
                      {slotToBlock.duration} minutes
                      {!slotToBlock.isUnblocking && slotToBlock.isPartialBlock && (
                        <span className="text-xs ml-1">({slotToBlock.requestedDuration} mins requested)</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{slotToBlock.isUnblocking ? 'Blocked Slots:' : 'Slots to Block:'}</span>
                    <span className={`font-semibold ${slotToBlock.isPartialBlock && !slotToBlock.isUnblocking ? 'text-yellow-700' : 'text-gray-900'}`}>
                      {Math.ceil(slotToBlock.duration / 15)} slots
                      {!slotToBlock.isUnblocking && slotToBlock.isPartialBlock && (
                        <span className="text-xs ml-1">({slotToBlock.requestedSlots} slots requested)</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
              
              {slotToBlock.isUnblocking ? (
                <p className="text-sm text-gray-600">
                  This will <strong>unblock {Math.ceil(slotToBlock.duration / 15)} consecutive time slots</strong> starting from {slotToBlock.timeSlot}, making them available for customer reservations.
                </p>
              ) : (
                <p className="text-sm text-gray-600">
                  This will <strong>block {Math.ceil(slotToBlock.duration / 15)} consecutive time slots</strong> starting from {slotToBlock.timeSlot}, preventing any reservations during this period.
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowBlockSlotModal(false);
                  setSlotToBlock(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              {slotToBlock.isUnblocking ? (
                <button
                  onClick={confirmUnblockSlot}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Unblocking...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Unblock Slot
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={confirmBlockSlot}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2"
                  style={{ backgroundColor: primaryColor }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Blocking...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Block Slot
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Day Detail Modal - Time Slots */}
      {showDayDetailModal && selectedDayDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: primaryColor }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {selectedDayDetail.table.table_name} - Time Slots
                  </h3>
                  <p className="text-sm text-white text-opacity-90 mt-0.5">
                    {selectedDayDetail.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowDayDetailModal(false);
                    setSelectedDayDetail(null);
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {generateTimeSlots().map((timeSlot) => {
                  const reservation = isTimeSlotBooked(selectedDayDetail.table.id, selectedDayDetail.date, timeSlot);
                  const isBooked = reservation !== null;
                  const isBlocked = reservation?.status === 'blocked';
                  
                  return (
                    <div
                      key={timeSlot}
                      className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                        isBooked
                          ? isBlocked
                            ? 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                            : 'bg-red-50 border-red-200 hover:bg-red-100'
                          : 'bg-green-50 border-green-200 hover:bg-green-100'
                      }`}
                      onClick={() => {
                        if (isBlocked) {
                          // Show unblock modal for blocked slots
                          setSlotToBlock({
                            table: selectedDayDetail.table,
                            date: selectedDayDetail.date,
                            timeSlot,
                            duration: reservation.duration_minutes || settings?.reservation_duration || 60,
                            reservationId: reservation.id,
                            isUnblocking: true
                          });
                          setShowBlockSlotModal(true);
                        } else if (isBooked) {
                          // Show full reservation details for regular bookings
                          handleViewReservation(reservation);
                        } else {
                          // Show block modal for available slots
                          handleBlockSlotClick(selectedDayDetail.table, selectedDayDetail.date, timeSlot);
                        }
                      }}
                      title={isBooked ? (isBlocked ? 'Blocked - Click to unblock' : 'Click to view reservation details') : 'Click to block this slot'}
                    >
                      <div className="text-center">
                        <div className="text-sm font-semibold text-gray-900">
                          {timeSlot}
                        </div>
                        <div className={`text-xs mt-1 font-medium ${
                          isBooked ? (isBlocked ? 'text-gray-600' : 'text-red-600') : 'text-green-600'
                        }`}>
                          {isBooked ? (isBlocked ? 'BLOCKED' : 'BOOKED') : 'Available'}
                        </div>
                        {isBooked && !isBlocked && reservation.form_details?.name && (
                          <div className="text-xs text-gray-600 mt-1 truncate">
                            {reservation.form_details.name}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-center gap-4 text-sm flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-50 border-2 border-green-200 rounded"></div>
                    <span className="text-gray-700">Available</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-50 border-2 border-red-200 rounded"></div>
                    <span className="text-gray-700">Booked</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-50 border-2 border-gray-300 rounded"></div>
                    <span className="text-gray-700">Blocked</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 text-center mt-2">
                  Click available slots to block • Click blocked slots to unblock • Click booked slots to view details
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={() => {
                  setShowDayDetailModal(false);
                  setSelectedDayDetail(null);
                }}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTables;